/**
 * defineSyncEntity — the universal factory for syncable entities.
 *
 * Define an entity schema once. Get server push handling, LWW conflict
 * resolution, sync-diff application, dirty detection, flush logic —
 * all generated automatically.
 *
 * Two entity types:
 *   - "array"  — a collection with id-based CRUD (folders, labels)
 *   - "value"  — a single LWW value (settings)
 */

import { z } from 'zod';

import { isNewerDocument, mergeItemsLWW } from '../framework/sync-merge';
import type { FieldScope } from '../schema/field-scopes';
import type { SyncClientMessage, SyncServerMessage } from '../schema/sync-types';
import { projectEntity } from './projection';

/** Delimiter between the parent and child segments of a record entry's
 *  compound key (e.g. `deckId\x01cardId`). Anything that lives at this
 *  boundary — entry-key parsing, nested-state grouping — uses this. */
const KEY_DELIM = '\x01';

// ---------------------------------------------------------------------------
// Minimal interfaces for cross-boundary types
// ---------------------------------------------------------------------------

/** A document stored in DO storage — has an LWW timestamp and payload fields. */
export type StorageDoc = {
  updatedAtISO?: string;
  deletedTimeISO?: string | null;
  [key: string]: unknown;
};

/** An LWW-trackable item (used in collections and record entries). */
export type LWWItem = {
  updatedAtISO?: string;
  deletedTimeISO?: string | null;
  [key: string]: unknown;
};

/** An incoming push/broadcast message (dynamic payload keyed by the entity's dataKey). */
export type SyncMsgEnvelope = {
  type: string;
  updatedAtISO?: string;
  key?: string;
  entry?: LWWItem;
  [key: string]: unknown;
};

/** Minimal DO storage interface (subset of Cloudflare DurableObjectStorage). */
export interface DOStorage {
  get(key: string): Promise<StorageDoc | undefined>;
  get(keys: string[]): Promise<Map<string, StorageDoc | undefined>>;
  get(
    keyOrKeys: string | string[],
  ): Promise<StorageDoc | Map<string, StorageDoc | undefined> | undefined>;
  put(key: string, value: StorageDoc): Promise<void>;
  /** Batched put — Cloudflare DurableObjectStorage native form. One round
   *  trip for many writes. Used by the bulk record handlers. */
  put(entries: Record<string, StorageDoc>): Promise<void>;
  delete(key: string | string[]): Promise<boolean>;
}

/**
 * Batch size for the sync-init per-entry diff loaders' multi-key gets.
 * One unbounded `get(keys[])` over a whole entity loads every body into
 * the DO isolate at once — the large-account OOM funnel — and Cloudflare
 * documents 128 keys as the per-get maximum. Exported for tests.
 */
export const DIFF_GET_CHUNK = 100;

/** Minimal client storage interface (subset of SyncStorage from src/).
 *  Writes are fire-and-forget (void) — the underlying adapter coalesces
 *  via a microtask ring buffer. Callers needing durable completion call
 *  `flush()` on the full SyncStorage. */
export interface ClientStorage {
  getAll<T>(table: string): Promise<T[]>;
  getByKey<T>(table: string, key: string | unknown[]): Promise<T | undefined>;
  put<T>(table: string, item: T): void;
  replaceAll<T>(table: string, items: T[]): void;
}

// ---------------------------------------------------------------------------
// Config types
// ---------------------------------------------------------------------------

type MessageConfig = {
  type: string;
  dataKey: string;
};

/**
 * Which Durable Object hosts the canonical state for this entity.
 *
 *   - `user`  — lives only in UserDo. Default for entities that have
 *     no group counterpart (`settings`, `userInfo`, `quizProgress`,
 *     `annotations`, `groupMemberships`).
 *   - `group` — lives only in StudyGroupDo. Used for the SG-only
 *     entities (`sgMeta`, `sgMembers`, `sgFolders`, `sgActivity`).
 *   - `both`  — lives in both authorities. Pair with `mirror` to
 *     specify how the two authorities' state relates.
 *
 * The registry-builders (`entity-registry.ts`,
 * `group-entity-registry.ts`) filter the unified schema by this field
 * so each DO only sees the entities it's expected to dispatch.
 */
export type EntityAuthority = 'user' | 'group' | 'both';

/**
 * For `authority: 'both'` entities, how do the two authorities' rows
 * relate?
 *
 *   - `independent` — each authority holds its OWN collection. Same
 *     entity shape on both sides but writes don't cross-propagate. No
 *     entity declares this today; reserved for future use (e.g. if
 *     `folders` were ever unified across both DOs).
 *
 *   - `split` — ONE logical row whose fields are partitioned by
 *     `fieldScopes`. UserDo holds identity + shared + personal (the
 *     union); StudyGroupDo holds identity + shared only. Writes to
 *     shared-scope fields fan out from UserDo to linked SGs and back
 *     to other members' UserDos. Phase C slice 2/3 will declare
 *     `cards` and `deckProperties` this way.
 *
 * Default: `independent` (when `authority: 'both'`). Ignored when
 * `authority` is `user` or `group`.
 */
export type EntityMirror = 'independent' | 'split';

type BaseEntityInput = {
  /** Primary identifier. Used to derive defaults for all other keys. */
  name: string;
  /**
   * Durable Object authority that hosts canonical state for this entity.
   * Defaults to `'user'` when omitted (matches the historical behavior
   * before the unified registry — every entity in `entitySchema.ts`
   * used to be UserDo-only).
   */
  authority?: EntityAuthority;
  /**
   * When `authority: 'both'`, how do the two authorities' rows relate?
   * See `EntityMirror`. Defaults to `'independent'` (no fanout).
   * Ignored when `authority` is `user` or `group`.
   *
   * Setting `mirror: 'split'` requires `fieldScopes` — the factory
   * throws at construction time otherwise so a half-configured entity
   * can't ship.
   */
  mirror?: EntityMirror;
  /** Key in the Zustand store / SyncSnapshot. Defaults to `name`. */
  stateKey?: string;
  /** Timestamp tracking key. Defaults to `__${name}`. */
  tsKey?: string;
  /** DO storage key. Defaults to `name`. */
  storageKey?: string;
  /** Key in the sync-diff response. Defaults to `name`. */
  diffKey?: string;
  /**
   * Read-only on the client: the DO is the sole writer, so the client
   * never emits a push/delete for this entity. `flushLocal` and
   * `detectDirty` skip it; the server's `authorize` gate rejects any push
   * that still arrives.
   */
  serverAuthored?: boolean;
  /** Message configs. Defaults to `{name}-push` / `{name}-updated` with dataKey = `name`. */
  messages?: {
    push: MessageConfig;
    broadcast: MessageConfig;
  };
  /**
   * Per-authority overrides for `mirror: 'split'` entities — the
   * StudyGroupDo side of the entity uses different storage keys, ts
   * keys, and wire-message names than the UserDo side.
   *
   * `groupStorageKey` overrides the default `storageKey` (used to
   * derive `groupTsKey`, `groupDoPrefix`, and `groupTsKeyPrefix` for
   * record entities).
   *
   * `groupMessages` overrides `messages` on the SG side.
   *
   * Both are REQUIRED when `mirror: 'split'`; the factory throws
   * otherwise. Ignored for non-split entities.
   *
   * Example (as used by `deckProperties`):
   *   storageKey:      'deckmeta',
   *   groupStorageKey: 'sgdeckmeta',
   *   messages:        { push: 'deckProperties-push',     ... },
   *   groupMessages:   { push: 'sg-deckProperties-push',  ... },
   */
  groupStorageKey?: string;
  groupMessages?: {
    push: MessageConfig;
    broadcast: MessageConfig;
  };
  /**
   * Per-authority overrides for record entities' delete message types.
   * Required when `mirror: 'split'` AND the entity declares `deleteType`
   * (so the SG side has a parallel delete message name).
   */
  groupDeleteType?: string;
  groupDeleteBroadcastType?: string;
  /**
   * Zod schema for the entity payload (single item/value/entry — NOT the
   * full message envelope). Used to build inbound message validators at
   * `onCustomMessage` dispatch time so malformed or crafted payloads are
   * rejected before touching DO storage.
   * Optional for now to keep non-validated entities backward-compatible,
   * but every new entity should supply one.
   */
  validationSchema?: z.ZodTypeAny;
  /**
   * op-sqlite table layout. Consumed by `framework/storage-layout.ts` to
   * auto-derive the RN typed-table-router catalogue and the migrator's
   * idempotent `CREATE TABLE IF NOT EXISTS` phase.
   *
   * Defaults (when omitted):
   *   - `tableName`: `snake_case(storageKey) + '_v2'`
   *   - `pk`: `[primaryKey]` for collection/array; `['key']` for value;
   *           REQUIRED for record (can't be inferred from `entryKey`)
   *   - `persist`: `true`
   *
   * Set `persist: false` to opt the entity out of op-sqlite persistence
   * (e.g. value entities that don't need a typed table).
   */
  storage?: StorageInput;
  /**
   * Record-only: opt into a nested in-memory state shape
   * `Record<parentId, Record<childPart, T>>` derived from the entry's
   * compound key (`parentId\x01childPart`). Cheap O(1) parent-bucket
   * access; preserves per-bucket reference equality so Zustand selectors
   * subscribing to a single parent don't re-render on every sibling
   * mutation. Today used by `cards` (`deckId\x01cardId`).
   *
   * Storage on disk stays flat — the converter runs at the
   * hydrate/persist/applySyncDiff/flushLocal/detectDirty boundaries.
   */
  nested?: boolean;
  /**
   * Per-field classification of this entity's payload as `shared` or
   * `personal`. Consumed by the scope-routing engine to:
   *   - On write: split a patch into (shared → SG room, personal → UserDo)
   *     based on the parent's `linkedGroups`.
   *   - On read: merge incoming patches into the local row honouring
   *     `sharedUpdatedAtISO` vs `personalUpdatedAtISO` per-scope LWW.
   *
   * Only meaningful for entities that participate in study-group sharing
   * (today: `cards`, `deckProperties`). Omit otherwise.
   */
  fieldScopes?: Readonly<Record<string, FieldScope>>;
  /**
   * Optional authorization predicate. The dispatch layer calls this with
   * the incoming row, its parent (when resolvable), and an `AuthContext`
   * carrying the requesting user. Returning `false` silently rejects the
   * write — matches today's `canEditSGDeck` shape.
   */
  authorize?: AuthorizeHook;
};

/**
 * Storage layout hints for an entity's op-sqlite table. All fields are
 * optional except `pk` for record entities (whose PK column names can't
 * be inferred from the `entryKey` function).
 */
export type StorageInput = {
  /** Override the default `snake_case(storageKey) + '_v2'`. */
  tableName?: string;
  /** camelCase TS field names that form the PK, in order. */
  pk?: readonly string[];
  /** Set false to skip op-sqlite persistence. Default true. */
  persist?: boolean;
};

/**
 * Context passed to the entity's `authorize` hook at dispatch time.
 * Currently just the requesting `userId`; can grow to include role,
 * permission flags, request metadata as needed.
 */
export type AuthContext = {
  userId: string;
};

/**
 * Per-entity authorization predicate. Returns `true` to allow the write,
 * `false` to silently reject it (matches today's `canEditSGDeck` shape).
 *
 * Signature is intentionally loose at the framework level. Each entity
 * narrows its own `row` / `parent` types in the declaration site.
 */
export type AuthorizeHook = (
  row: unknown,
  parent: unknown,
  context: AuthContext,
) => boolean;

export type ArrayEntityInput<T = unknown[]> = BaseEntityInput & {
  type: 'array';
  primaryKey: string;
  initial: T;
};

/**
 * A collection is like an array but with **per-item LWW conflict resolution**.
 *
 * Each item carries its own `updatedAtISO` timestamp. Mutations stamp
 * the affected item, dirty detection sends only changed items, and the
 * server merges per-item instead of replacing the whole blob.
 *
 * Items should have `updatedAtISO?: string` and `deletedTimeISO?: string | null`
 * for soft deletes.
 */
export type CollectionEntityInput<T = unknown[]> = BaseEntityInput & {
  type: 'collection';
  primaryKey: string;
  initial: T;
  /**
   * When true, the entity's in-memory state is `Record<primaryKey, TItem>`
   * (indexed by id) instead of the default `TItem[]` (array). Storage on
   * disk stays array-shaped — the factory converts at the hydrate /
   * persist boundary. Use for collections where O(1) per-id lookup is
   * required (today: `deckProperties`).
   */
  indexById?: boolean;
};

export type ValueEntityInput<T = unknown> = BaseEntityInput & {
  type: 'value';
  /** Settings key used for getValue/putValue. Defaults to `name`. */
  settingsKey?: string;
  initial: T;
};

/**
 * A record entity is a `Record<string, TEntry>` where each entry is
 * independently timestamped, stored, synced, and merged. Unlike `collection`
 * (array with per-item LWW, one server blob), `record` stores each entry
 * as a separate DO key and tracks per-entry timestamps.
 */
export type RecordEntityInput<TEntry = unknown> = BaseEntityInput & {
  type: 'record';
  /** Derive the string key from an entry. */
  entryKey: (entry: TEntry) => string;
  /** DO storage prefix for per-entry keys (e.g. "quiz:"). */
  doPrefix: string;
  /** Per-entry timestamp prefix (e.g. "__quiz:"). */
  tsKeyPrefix: string;
  /** Optional custom per-entry merge (default: LWW by updatedAtISO). */
  mergeEntry?: (base: TEntry, incoming: TEntry) => TEntry;
  /**
   * Let the CLIENT fold a peer's broadcast row into the row it holds with
   * `mergeEntry` — even when the local row is NOT dirty — and leave the row
   * dirty when the merge kept something the broadcast lacked, so the next
   * flush pushes the union. Without it a broadcast REPLACES the local row
   * whole and stamps its clock (the default every entity had before the
   * materials index existed).
   *
   * Opt in ONLY for a merge that is a true join: idempotent, commutative,
   * and with every sub-entry carrying its OWN clock, so `merge(broadcast,
   * local)` on a stale local copy can never re-assert local content over
   * the peer's newer content. `mergeQuizEntry` is NOT such a merge — an
   * attempt both sides hold resolves to the INCOMING copy (its `ts` is an
   * identity, not a clock), so folding a peer's quiz rename into a stale
   * local row would overwrite the rename, mark the row dirty, and push the
   * old name back — ping-pong on every reconnect. `deckProperties` is:
   * its notes/maps entries are clocked per entry (`deck-materials-merge.ts`).
   */
  foldBroadcasts?: boolean;
  /**
   * Optional ingest guard applied to every server-sourced entry in
   * `applySyncDiff` BEFORE merge/apply. `key` is the diff key (the entry's
   * addressing key on the server). Return the entry (same reference or a
   * replacement) to accept it, or `null` to drop it entirely — a dropped
   * entry is neither applied nor timestamped, so a later repaired server
   * row still delivers. Used by `deckProperties` to drop malformed rows
   * (pre-5.3b `DODeckMeta` envelopes, id-less minimal metas) before they
   * can reach the store — see `schema/deck-meta-guard.ts`.
   */
  normalizeServerEntry?: (key: string, entry: unknown) => TEntry | null;
  /** Field in sync-diff carrying all server keys. */
  serverKeysField?: string;
  /** Delete message type string (e.g. "quiz-delete"). */
  deleteType?: string;
  /** Delete broadcast type string (e.g. "quiz-deleted"). */
  deleteBroadcastType?: string;
  /**
   * Decompose the composite string key into the storage-layer primary key.
   * Required when the local DB uses a compound primary key (e.g. Dexie
   * `[deckId+quizTs]`). If omitted, the string key is passed as-is.
   */
  decomposeStorageKey?: (key: string) => unknown;
  initial: Record<string, TEntry>;
};

export type SyncEntityInput<T = unknown> =
  | ArrayEntityInput<T>
  | CollectionEntityInput<T>
  | ValueEntityInput<T>
  | RecordEntityInput<LWWItem>;

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export type SyncEntity<
  T = unknown,
  Name extends string = string,
  Kind extends 'array' | 'collection' | 'record' | 'value' =
    | 'array'
    | 'collection'
    | 'record'
    | 'value',
> = {
  // ── Identity & config ───────────────────────────────────────────────

  /** Primary identifier. Used to generate Zustand method names (e.g. "folders" → setFolders, addFolder). */
  name: Name;

  /**
   * Which Durable Object hosts canonical state for this entity. See
   * `EntityAuthority`. Defaults to `'user'`. Consumed by the
   * registry-builders to partition the unified `entitySchema` into
   * per-DO dispatch maps.
   */
  authority: EntityAuthority;
  /**
   * For `authority: 'both'` entities, how the two sides' rows relate.
   * See `EntityMirror`. Always set (defaults to `'independent'`) so
   * consumers don't need to check `authority` first to interpret it.
   */
  mirror: EntityMirror;

  /**
   * Key in the Zustand store and SyncSnapshot.
   * Read by: flashka-store (hydrate/reset), sync-engine (applySyncDiff, flushLocal, detectDirty),
   * userdo (building genericDiffObj), client entity wrapper (Zustand mutations).
   */
  stateKey: string;

  /**
   * Key used to track this entity's server-synced timestamp (e.g. "__folders").
   * Read by: sync-engine (buildSyncInit), userdo (clientTsMap for sync-init),
   * client entity wrapper (recording timestamp after broadcast).
   */
  tsKey: string;

  /**
   * Key used inside the `sync-diff.generics` bucket on the wire. Defaults to
   * `stateKey`. Most entities leave this equal to `stateKey`; `settings` is
   * the historical exception (`stateKey=globalSrSettings`, `diffKey=settings`).
   */
  diffKey: string;

  /**
   * Key for DO storage (server) and IndexedDB table name (client arrays) or
   * settings key name (client values).
   * Read by: server handlePush/loadForDiff (DO get/put), client hydrate/persistReplace,
   * client CRUD (put/updateByKey/removeByKey on this table).
   */
  storageKey: string;

  /** "array" = id-based CRUD. "collection" = per-item LWW. "record" = per-entry DO keys. "value" = single LWW. */
  type: Kind;

  /** For array entities: the field used as primary key (e.g. "id"). Undefined for value entities. */
  primaryKey?: string;

  /** For collection entities with `indexById: true`: state is keyed by `primaryKey`. */
  indexById?: boolean;

  /** Default/empty value. Used as fallback when IndexedDB returns undefined during hydration, and as initial Zustand state. */
  initial: T;

  /** Raw input config preserved for reference/debugging. Not consumed at runtime. */
  input: SyncEntityInput<T>;

  // ── Generic primitives (state shape, scope routing, auth) ──────────

  /**
   * Record-only: state shape is `Record<parentId, Record<childPart, T>>`
   * keyed by the first segment of the entry's compound key. See
   * `BaseEntityInput.nested`.
   */
  nested?: boolean;
  /**
   * Nested-record only: the entry-body field that mirrors the parent
   * segment of the compound key (e.g. `'deckId'` for cards). The wire
   * format encodes the parent in the key and may omit it from the entry
   * body; client-side persist sites use this to stamp the field back
   * onto the row before writing to local storage, so subsequent
   * hydration can bucket by parent. Derived from `storage.pk[0]`.
   */
  parentKeyField?: string;
  /** Per-field scope classification for split-canonical routing. */
  fieldScopes?: Readonly<Record<string, FieldScope>>;
  /** Authorization hook called at dispatch time; see `AuthorizeHook`. */
  authorize?: AuthorizeHook;

  // ── Record-specific fields (undefined for array/collection/value) ───

  /** Derive string key from an entry (record entities only). */
  entryKey?: (entry: LWWItem) => string;
  /** DO storage prefix (record entities only, e.g. "quiz:"). */
  doPrefix?: string;
  /** Per-entry timestamp prefix (record entities only, e.g. "__quiz:"). */
  tsKeyPrefix?: string;
  /** Delete message type (record entities only, e.g. "quiz-delete"). */
  deleteType?: string;
  /** Delete broadcast type (record entities only, e.g. "quiz-deleted"). */
  deleteBroadcastType?: string;
  /** Field in sync-diff carrying all server keys (record entities only). */
  serverKeysField?: string;
  /** Ingest guard for server-sourced entries — see
   *  `RecordEntityInput.normalizeServerEntry`. Exposed on the built entity
   *  so the CLIENT broadcast handlers can apply the same guard as the
   *  sync-diff path (record entities only). */
  normalizeServerEntry?: (key: string, entry: unknown) => LWWItem | null;
  /** Custom per-entry merge — see `RecordEntityInput.mergeEntry`. Exposed on
   *  the built entity so the CLIENT broadcast handlers fold a peer's row into
   *  the local one the way the server and the sync-diff path do, instead of
   *  replacing it whole (record entities only). */
  mergeEntry?: (base: LWWItem, incoming: LWWItem) => LWWItem;
  /** The client folds broadcasts through `mergeEntry` instead of replacing
   *  the local row — see `RecordEntityInput.foldBroadcasts` for the bar an
   *  entity must clear to declare it (record entities only). */
  foldBroadcasts?: boolean;
  /** Decompose composite string key into storage-layer primary key (record entities only). */
  decomposeStorageKey?: (key: string) => unknown;

  // ── Message factories ───────────────────────────────────────────────

  /** Client→Server push message type string (e.g. "folders-push"). Used by entity-registry to build pushHandlerMap for dispatch. */
  pushType: string;

  /** Server→Client broadcast message type string (e.g. "folders-updated"). Used by entity-registry to build broadcastHandlerMap for dispatch. */
  broadcastType: string;

  // ── Bulk wires (record entities only) ──────────────────────────────
  //
  // Auto-derived from `pushType` / `broadcastType` / `deleteType` for
  // every `type: 'record'` entity. The bulk envelope carries an
  // `entries: Array<{key, entry, updatedAtISO}>` array so flushLocal +
  // detectDirty emit ONE frame per chunk instead of N per-entry. Bulk
  // delete carries `keys: Array<{key, deletedTimeISO}>`.
  //
  // Naming convention: `${name}-push` → `${name}-bulk-push`. For entities
  // that override `messages.push.type` (e.g. quizProgress → 'quiz-push'),
  // the bulk wire derives from the OVERRIDDEN type ('quiz-push' →
  // 'quiz-bulk-push'), not from `name`.
  //
  // Undefined for non-record entities (collection/array/value already
  // bulk by nature — they push the whole collection in one envelope).
  /** Bulk push message type (record entities only). */
  bulkPushType?: string;
  /** Bulk broadcast type (record entities only). */
  bulkBroadcastType?: string;
  /** Bulk delete type (record entities with `deleteType` only). */
  bulkDeleteType?: string;
  /** Bulk delete broadcast type. */
  bulkDeleteBroadcastType?: string;

  // ── Per-authority overrides (mirror: 'split' only) ─────────────────
  //
  // Populated by the factory when the entity declares `mirror: 'split'`
  // alongside `groupStorageKey` + `groupMessages`. The StudyGroupDo
  // registry-builder reads these instead of the top-level
  // `storageKey` / `tsKey` / `pushType` / `broadcastType` when
  // assembling dispatch maps for split entities. Undefined for
  // non-split entities.

  /** SG-side override of `storageKey`. */
  groupStorageKey?: string;
  /** SG-side override of `tsKey`. Derived from `groupStorageKey`. */
  groupTsKey?: string;
  /** SG-side override of `pushType`. */
  groupPushType?: string;
  /** SG-side override of `broadcastType`. */
  groupBroadcastType?: string;
  /** SG-side override of `doPrefix` (record entities). */
  groupDoPrefix?: string;
  /** SG-side override of `tsKeyPrefix` (record entities). */
  groupTsKeyPrefix?: string;
  /** SG-side override of `deleteType` (record entities). */
  groupDeleteType?: string;
  /** SG-side override of `deleteBroadcastType` (record entities). */
  groupDeleteBroadcastType?: string;
  /** SG-side bulk push type (`mirror: 'split'` record entities). */
  groupBulkPushType?: string;
  /** SG-side bulk broadcast type. */
  groupBulkBroadcastType?: string;
  /** SG-side bulk delete type. */
  groupBulkDeleteType?: string;
  /** SG-side bulk delete broadcast type. */
  groupBulkDeleteBroadcastType?: string;

  /**
   * Group-side handlePush for `mirror: 'split'` record entities.
   *
   * Mirrors `handlePush` but:
   *   1. Projects the incoming entry through `projectEntity(_, 'group', fieldScopes)`
   *      — strips personal-scope keys at the storage boundary.
   *   2. Writes to `groupDoPrefix + key` (e.g. `sgdeckmeta\x01{id}`)
   *      instead of `doPrefix + key`.
   *   3. Broadcasts via the SG-side wire types (`groupBroadcastType`,
   *      `groupDeleteBroadcastType`).
   *
   * Undefined for non-split entities and for split non-record entities
   * (Phase C slice 4a scopes the new dispatch path to records only —
   * the entities that need it are `deckProperties` and eventually `cards`).
   *
   * The group-side registry-builder uses this in place of `handlePush`
   * when dispatching for a split entity, so the existing dispatch loop
   * in `SyncYServer.onCustomMessage` continues to call
   * `entity.handlePush(...)` without knowing the difference.
   */
  handleGroupPush?: (
    doStorage: DOStorage,
    broadcast: (msg: SyncServerMessage) => void,
    msg: unknown,
  ) => Promise<void>;

  /**
   * Group-side handleDelete for `mirror: 'split'` record entities.
   * Same LWW + tombstone semantics as `handleDelete`, but reads/writes
   * at `groupDoPrefix` and broadcasts via `groupDeleteBroadcastType`.
   */
  handleGroupDelete?: (
    doStorage: DOStorage,
    broadcast: (msg: SyncServerMessage) => void,
    msg: unknown,
  ) => Promise<void>;

  /**
   * Bulk push handler (record entities only). Loops `msg.entries`,
   * runs the existing per-entry LWW gate, batched storage.put for
   * accepted entries, and emits ONE bulk-updated broadcast carrying
   * only the accepted entries.
   */
  handleBulkPush?: (
    doStorage: DOStorage,
    broadcast: (msg: SyncServerMessage) => void,
    msg: unknown,
  ) => Promise<void>;

  /**
   * Bulk delete handler (record entities with `deleteType` only).
   * Tombstones each key in `msg.keys` (LWW vs existing row), batched
   * storage.put for tombstones, ONE bulk-deleted broadcast.
   */
  handleBulkDelete?: (
    doStorage: DOStorage,
    broadcast: (msg: SyncServerMessage) => void,
    msg: unknown,
  ) => Promise<void>;

  /**
   * Group-side bulk push handler (`mirror: 'split'` record entities).
   * Projects every entry through `projectEntity(_, 'group', fieldScopes)`
   * before applying LWW + writing at `groupDoPrefix`.
   */
  handleGroupBulkPush?: (
    doStorage: DOStorage,
    broadcast: (msg: SyncServerMessage) => void,
    msg: unknown,
  ) => Promise<void>;

  /**
   * Group-side bulk delete handler (`mirror: 'split'` record entities).
   */
  handleGroupBulkDelete?: (
    doStorage: DOStorage,
    broadcast: (msg: SyncServerMessage) => void,
    msg: unknown,
  ) => Promise<void>;

  /**
   * Group-side sync-init record loader for `mirror: 'split'` record
   * entities. Same shape as `loadForDiffRecord`, but reads from
   * `groupDoPrefix` instead of `doPrefix`. The SG sync-init builder
   * calls this for split entities; non-split entities go through
   * `loadForDiffRecord` as before.
   */
  loadForDiffGroupRecord?: (
    doStorage: DOStorage,
    keys: string[],
    clientTsMap: Record<string, string>,
  ) => Promise<Record<string, unknown> | undefined>;

  /**
   * Zod validator for the full inbound push message envelope (type +
   * payload + updatedAtISO). Undefined when no `validationSchema` was
   * supplied at define-time. Consumed by the server dispatch layer —
   * validate BEFORE any storage write.
   */
  pushMessageValidator?: z.ZodTypeAny;

  /**
   * Zod validator for the full inbound delete message envelope (record
   * entities only, and only when `deleteType` + `validationSchema` are
   * set). Consumed identically to `pushMessageValidator`.
   */
  deleteMessageValidator?: z.ZodTypeAny;

  /**
   * Group-side push validator for `mirror: 'split'` record entities.
   * Same shape as `pushMessageValidator` but its `type` literal is the
   * group-side push type (e.g. `sg-deckProperties-push`) so an
   * envelope arriving on the SG socket validates correctly. The
   * group-entity-registry swaps this in via `adaptForGroup`.
   */
  groupPushMessageValidator?: z.ZodTypeAny;

  /**
   * Group-side delete validator for `mirror: 'split'` record entities
   * (only when the entity declares a delete type).
   */
  groupDeleteMessageValidator?: z.ZodTypeAny;

  /** Bulk push envelope validator (record entities only). */
  bulkPushMessageValidator?: z.ZodTypeAny;
  /** Bulk delete envelope validator (record entities with `deleteType`). */
  bulkDeleteMessageValidator?: z.ZodTypeAny;
  /** Group-side bulk push validator (`mirror: 'split'` records). */
  groupBulkPushMessageValidator?: z.ZodTypeAny;
  /** Group-side bulk delete validator. */
  groupBulkDeleteMessageValidator?: z.ZodTypeAny;

  /** Build a push message: wraps data + updatedAtISO with the push message type. Used by flushLocal and detectDirty to create outbound messages. */
  createPushMessage: (data: T, updatedAtISO: string) => SyncClientMessage;

  /** Build a broadcast message: wraps data + updatedAtISO with the broadcast message type. Used by handlePush to broadcast to other clients. */
  createBroadcastMessage: (data: T, updatedAtISO: string) => SyncServerMessage;

  /** Extract the data payload from an incoming push/broadcast message (reads msg[dataKey]). Used by handlePush (server) and applyServerBroadcast (client). */
  extractFromMessage: (msg: unknown) => T;

  // ── Server-side (runs in DO / Cloudflare Workers) ───────────────────

  /** Handle an incoming client push: LWW check against existing → store → broadcast to other clients. Called by userdo.ts via pushHandlerMap dispatch. */
  handlePush: (
    doStorage: DOStorage,
    broadcast: (msg: SyncServerMessage) => void,
    msg: unknown,
  ) => Promise<void>;

  /** Load server data for sync-diff if server version is newer than client's timestamp. Called by userdo.ts during handleSyncInit. Returns the stored blob or undefined. */
  loadForDiff: (doStorage: DOStorage, clientTs: string | undefined) => Promise<unknown>;

  /** Handle a delete request (record entities only). */
  handleDelete?: (
    doStorage: DOStorage,
    broadcast: (msg: SyncServerMessage) => void,
    msg: unknown,
  ) => Promise<void>;

  /** Load per-entry data for sync-diff (record entities only). */
  loadForDiffRecord?: (
    doStorage: DOStorage,
    keys: string[],
    clientTsMap: Record<string, string>,
  ) => Promise<Record<string, unknown> | undefined>;

  /** Build a delete push message (record entities only). When
   * `deletedTimeISO` is omitted the factory stamps `now()` — but callers
   * SHOULD pass the actual delete time so the server can LWW-compare. */
  createDeleteMessage?: (key: string, deletedTimeISO?: string) => SyncClientMessage;

  /** Build a delete broadcast message (record entities only). */
  createDeleteBroadcastMessage?: (
    key: string,
    deletedTimeISO?: string,
  ) => SyncServerMessage;

  // ── Sync engine (pure functions, shared by server & client) ─────────

  /** Extract this entity's data from a sync-diff response and update timestamps. For collection entities, `localData` is merged per-item with server data instead of replacing. */
  applySyncDiff: (
    diff: unknown,
    ts: Record<string, string>,
    localData?: T,
  ) => T | undefined;

  /** Push local data to server if the server hasn't seen it yet (no server timestamp exists). Called by sync-engine.ts applySyncDiff after the diff is processed. */
  flushLocal: (
    diff: unknown,
    localData: T,
    ts: Record<string, string>,
    outbound: SyncClientMessage[],
  ) => void;

  /** Compare prev and curr Zustand snapshots; if this entity changed, create a push message. Called by sync-engine.ts detectDirtyChanges on every store change.
   *  Every timestamp it advances is WRITTEN to `ts`. With `baseTs`, `ts` is a
   *  delta over it: a key `ts` does not hold reads through to `baseTs`, so the
   *  caller never has to copy the library-wide map just to diff one tick. */
  detectDirty: (
    prev: T,
    curr: T,
    ts: Record<string, string>,
    outbound: SyncClientMessage[],
    baseTs?: Readonly<Record<string, string>>,
  ) => void;

  // ── Client-side persistence (IndexedDB via SyncStorage) ─────────────

  /** Load this entity from IndexedDB on app startup. For values: getSetting(settingsKey). For arrays: getAll(storageKey). Called by flashka-store hydrate. */
  hydrate: (storage: ClientStorage) => Promise<T>;

  /** Replace this entity's data in IndexedDB. For values: putSetting(settingsKey, data). For arrays: replaceAll(storageKey, data). Called by client CRUD setters and broadcast handlers. */
  persistReplace: (storage: ClientStorage, data: T) => void;
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

const EPOCH = new Date(0).toISOString();

// ---------------------------------------------------------------------------
// State-shape converters — flat ↔ nested for record entities,
// array ↔ indexed for collection entities. Used at the hydrate/persist/
// applySyncDiff/flushLocal/detectDirty boundaries when the entity opts
// into a non-default state shape (`nested` for records,
// `indexById` for collections).
// ---------------------------------------------------------------------------

/**
 * Record-slice walkers. A record entity's state is either flat
 * (`Record<key, T>`) or, with `nested: true`, `Record<parentId, Record<childPart, T>>`
 * keyed by the composite `${parentId}\x01${childPart}`. The engine's per-entry
 * logic (detectDirty / flushLocal / applySyncDiff) used to FLATTEN a nested
 * slice into a fresh flat map first — for a 27k-card library that was two
 * 27k-key allocations on EVERY local card write. These walkers visit the
 * nested shape in place instead.
 *
 * CRITICAL: they preserve ENTRY REFERENCES. Zustand slices like
 * `cards[deckId][cardId]` store the deckId as the bucket key, not on the row,
 * so the composite key is derived from `(parentId, childKey)` and the row is
 * never re-wrapped — a fresh wrapper per entry would break the
 * `entry === prevEntry` short-circuit and push every existing card on any
 * write (see `nested-state.spec.ts` "REGRESSION: adding one card with N
 * existing cards").
 */

/** Visit every `(compositeKey, entry)` of a record slice. */
function forEachRecordEntry(
  data: unknown,
  nested: boolean,
  visit: (key: string, entry: LWWItem) => void,
): void {
  if (!data) return;
  if (!nested) {
    for (const [key, entry] of Object.entries(data as Record<string, LWWItem>)) {
      visit(key, entry);
    }
    return;
  }
  for (const [parentId, bucket] of Object.entries(
    data as Record<string, Record<string, LWWItem>>,
  )) {
    for (const [childKey, entry] of Object.entries(bucket)) {
      visit(`${parentId}${KEY_DELIM}${childKey}`, entry);
    }
  }
}

/** Read one entry of a record slice by its composite key (no allocation). */
function lookupRecordEntry(
  data: unknown,
  nested: boolean,
  key: string,
): LWWItem | undefined {
  if (!data) return undefined;
  if (!nested) return (data as Record<string, LWWItem>)[key];
  const sep = key.indexOf(KEY_DELIM);
  if (sep === -1) return undefined;
  return (data as Record<string, Record<string, LWWItem> | undefined>)[
    key.slice(0, sep)
  ]?.[key.slice(sep + 1)];
}

/**
 * Whether a custom `mergeEntry` result carries anything the other entry
 * did not — decided on CONTENT, not identity, and blind to key order and
 * `undefined`-valued keys, both of which a spread-built merge changes freely.
 * Used on the server (a push whose merge changed nothing is neither written
 * nor broadcast) and on the client (a broadcast whose merge kept something
 * local leaves the row dirty, so the next flush pushes it).
 */
export function sameEntryContent(a: unknown, b: unknown): boolean {
  return a === b || stableJson(a) === stableJson(b);
}

/**
 * The clock a server-merged row is stored and broadcast under — for an
 * entity whose merge is a true join (`foldBroadcasts`).
 *
 * A device learns a row on reconnect only when the row's clock is newer than
 * the copy it holds (`loadForDiffRecord`). A union kept under the WINNER's
 * clock therefore never reaches a device that already holds the winner (or
 * a row stamped the same moment), nor the device whose push won — the server
 * does not echo a write back to its sender. That device would never list the
 * other side's offline note, attached document or removal. So when the merge
 * holds something an input lacked and that input's clock is not older than
 * the merged one, the row is stamped one millisecond after both inputs. The
 * stamp depends on the two rows alone (a re-push of the same pair lands on
 * the same clock) and a merge that adds nothing never reaches here.
 *
 * Server-only on purpose: a client's own merge keeps the winner's clock, so
 * an edit made on the device a moment later is never outranked by a stamp
 * the device invented. The client learns the stamped union through the diff
 * or the broadcast like any other newer row.
 */
function stampMergedAfterInputs(
  rec: { foldBroadcasts?: boolean },
  existing: LWWItem,
  incoming: LWWItem,
  merged: LWWItem,
): LWWItem {
  if (!rec.foldBroadcasts) return merged;
  const clock = merged.updatedAtISO ?? '';
  const behind = [existing, incoming].some(
    (input) =>
      (input.updatedAtISO ?? '') >= clock &&
      !sameEntryContent(
        { ...merged, updatedAtISO: undefined },
        { ...input, updatedAtISO: undefined },
      ),
  );
  if (!behind) return merged;
  const latest =
    (existing.updatedAtISO ?? '') >= (incoming.updatedAtISO ?? '')
      ? existing.updatedAtISO
      : incoming.updatedAtISO;
  const ms = latest ? Date.parse(latest) : Number.NaN;
  if (Number.isNaN(ms)) return merged;
  const after = new Date(ms + 1).toISOString();
  return after > clock ? { ...merged, updatedAtISO: after } : merged;
}

function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) => {
    if (v === null || typeof v !== 'object' || Array.isArray(v)) return v;
    const sorted: Record<string, unknown> = {};
    for (const k of Object.keys(v as Record<string, unknown>).sort()) {
      sorted[k] = (v as Record<string, unknown>)[k];
    }
    return sorted;
  });
}

/**
 * Visit the entries of `curr` whose reference differs from `prev`'s entry at
 * the same key. For a nested slice a parent bucket that kept its IDENTITY is
 * skipped whole: the store writes untouched decks by reference, so one card
 * write costs O(cards in that deck), not O(every resident card).
 */
function forEachChangedRecordEntry(
  prev: unknown,
  curr: unknown,
  nested: boolean,
  visit: (key: string, entry: LWWItem) => void,
): void {
  if (!curr) return;
  // `for…in` (not `Object.entries`): same own-key order on these plain store
  // objects, without allocating an [key, value] pair per deck and per card on
  // every local tick.
  if (!nested) {
    const prevRecord = (prev ?? {}) as Record<string, LWWItem>;
    const currRecord = curr as Record<string, LWWItem>;
    for (const key in currRecord) {
      if (!hasOwn(currRecord, key)) continue;
      const entry = currRecord[key];
      if (entry === prevRecord[key]) continue;
      visit(key, entry);
    }
    return;
  }
  const prevNested = (prev ?? {}) as Record<string, Record<string, LWWItem> | undefined>;
  const currNested = curr as Record<string, Record<string, LWWItem>>;
  for (const parentId in currNested) {
    if (!hasOwn(currNested, parentId)) continue;
    const bucket = currNested[parentId];
    const prevBucket = prevNested[parentId];
    if (bucket === prevBucket) continue;
    for (const childKey in bucket) {
      if (!hasOwn(bucket, childKey)) continue;
      const entry = bucket[childKey];
      if (prevBucket && entry === prevBucket[childKey]) continue;
      visit(`${parentId}${KEY_DELIM}${childKey}`, entry);
    }
  }
}

/**
 * Visit the keys present in `prev` but absent from `curr` — the deletions.
 * Same bucket-identity skip as above; a parent bucket missing from `curr`
 * yields every child key it had (removing a whole deck deletes its cards —
 * unchanged semantics).
 */
function forEachRemovedRecordKey(
  prev: unknown,
  curr: unknown,
  nested: boolean,
  visit: (key: string) => void,
): void {
  if (!prev) return;
  if (!nested) {
    const currRecord = (curr ?? {}) as Record<string, LWWItem>;
    const prevRecord = prev as Record<string, LWWItem>;
    for (const key in prevRecord) {
      if (!hasOwn(prevRecord, key)) continue;
      if (key in currRecord) continue;
      visit(key);
    }
    return;
  }
  const currNested = (curr ?? {}) as Record<string, Record<string, LWWItem> | undefined>;
  const prevNested = prev as Record<string, Record<string, LWWItem>>;
  for (const parentId in prevNested) {
    if (!hasOwn(prevNested, parentId)) continue;
    const prevBucket = prevNested[parentId];
    const currBucket = currNested[parentId];
    if (currBucket === prevBucket) continue;
    for (const childKey in prevBucket) {
      if (!hasOwn(prevBucket, childKey)) continue;
      if (currBucket && childKey in currBucket) continue;
      visit(`${parentId}${KEY_DELIM}${childKey}`);
    }
  }
}

const hasOwn = (o: object, k: string): boolean =>
  Object.prototype.hasOwnProperty.call(o, k);

function nestFlat(
  flat: Record<string, LWWItem>,
): Record<string, Record<string, LWWItem>> {
  const nested: Record<string, Record<string, LWWItem>> = {};
  for (const [key, entry] of Object.entries(flat)) {
    const sep = key.indexOf(KEY_DELIM);
    if (sep === -1) continue;
    const parentId = key.slice(0, sep);
    const childPart = key.slice(sep + 1);
    (nested[parentId] ??= {})[childPart] = entry;
  }
  return nested;
}

function indexByPk<TItem extends Record<string, unknown>>(
  items: readonly TItem[],
  pk: string,
): Record<string, TItem> {
  const out: Record<string, TItem> = {};
  for (const item of items) {
    const id = item[pk] as string | undefined;
    if (id !== undefined) out[id] = item;
  }
  return out;
}

export function defineSyncEntity<T>(input: SyncEntityInput<T>): SyncEntity<T> {
  const { name } = input;
  const isRecord = input.type === 'record';
  const recInput = isRecord
    ? (input as unknown as RecordEntityInput<LWWItem>)
    : undefined;

  // Resolve authority + mirror early so all downstream helpers can
  // reference them. The full invariant checks (which throw for
  // misconfigured split entities) run further below — these defaults
  // never lie, even if invariants haven't been verified yet.
  const resolvedAuthority: EntityAuthority = input.authority ?? 'user';
  const resolvedMirror: EntityMirror = input.mirror ?? 'independent';
  const isSplitRecord = isRecord && resolvedMirror === 'split';

  // Apply convention-based defaults derived from `name`
  const stateKey = input.stateKey ?? name;
  const tsKey = input.tsKey ?? `__${name}`;
  const storageKey = input.storageKey ?? name;
  const messages = input.messages ?? {
    push: { type: `${name}-push`, dataKey: name },
    broadcast: { type: `${name}-updated`, dataKey: name },
  };
  const diffKey = input.diffKey ?? stateKey;
  const pushDataKey = messages.push.dataKey;
  const broadcastDataKey = messages.broadcast.dataKey;

  const createPushMessage = (data: T, updatedAtISO: string) =>
    ({
      type: messages.push.type,
      [pushDataKey]: data,
      updatedAtISO,
    }) as unknown as SyncClientMessage;

  const createBroadcastMessage = (data: T, updatedAtISO: string) =>
    ({
      type: messages.broadcast.type,
      [broadcastDataKey]: data,
      updatedAtISO,
    }) as unknown as SyncServerMessage;

  const extractFromMessage = (msg: unknown): T =>
    (msg as Record<string, unknown>)[pushDataKey] as T;

  // Persistence via generic storage methods
  const resolvedSettingsKey = input.type === 'value' ? (input.settingsKey ?? name) : name;

  // Flags that switch the in-memory state shape (storage on disk stays flat).
  //
  //   - Record + nested:    state is `Record<parentId, Record<childPart, T>>`
  //   - Collection + indexById: state is `Record<primaryKey, T>`
  //
  // These let consumers preserve O(1) per-parent / per-id access while the
  // engine still hydrates/persists via the flat `ClientStorage` interface.
  const serverAuthored = Boolean(input.serverAuthored);
  const nestedRecord = isRecord && Boolean(input.nested);
  const indexedCollection =
    input.type === 'collection' && Boolean((input as CollectionEntityInput).indexById);
  const collectionPk =
    input.type === 'collection' ? (input as CollectionEntityInput).primaryKey : undefined;

  const hydrate = async (storage: ClientStorage): Promise<T> => {
    if (input.type === 'value') {
      const entry = await storage.getByKey<{ key: string; value: T }>(
        'settings',
        resolvedSettingsKey,
      );
      return entry?.value as T;
    }
    if (isRecord) {
      const entries = await storage.getAll<LWWItem>(storageKey);
      if (nestedRecord) {
        // Group flat rows into Record<parentId, Record<childPart, TEntry>>.
        // Parent id is the first segment of the entry key before `\x01`.
        const nested: Record<string, Record<string, LWWItem>> = {};
        for (const entry of entries) {
          const entryKey = recInput!.entryKey(entry);
          const sep = entryKey.indexOf(KEY_DELIM);
          if (sep === -1) continue;
          const parentId = entryKey.slice(0, sep);
          if (!parentId) continue;
          const childPart = entryKey.slice(sep + 1);
          (nested[parentId] ??= {})[childPart] = entry;
        }
        return nested as unknown as T;
      }
      const record: Record<string, LWWItem> = {};
      for (const entry of entries) {
        record[recInput!.entryKey(entry)] = entry;
      }
      return record as T;
    }
    if (indexedCollection && collectionPk) {
      const items = await storage.getAll<Record<string, unknown>>(storageKey);
      const indexed: Record<string, unknown> = {};
      for (const item of items) {
        const id = item[collectionPk] as string | undefined;
        if (!id) continue;
        indexed[id] = item;
      }
      return indexed as T;
    }
    return storage.getAll(storageKey) as Promise<T>;
  };

  const persistReplace = (storage: ClientStorage, data: unknown) => {
    if (input.type === 'value') {
      storage.put('settings', { key: resolvedSettingsKey, value: data });
    } else if (isRecord) {
      if (nestedRecord) {
        // Flatten Record<parentId, Record<childPart, TEntry>> → array.
        const nested = (data ?? {}) as Record<string, Record<string, LWWItem>>;
        const flat: LWWItem[] = [];
        for (const bucket of Object.values(nested)) {
          for (const entry of Object.values(bucket)) flat.push(entry);
        }
        storage.replaceAll(storageKey, flat);
        return;
      }
      storage.replaceAll(storageKey, Object.values(data as Record<string, unknown>));
    } else if (indexedCollection) {
      // Indexed → array for storage.
      storage.replaceAll(storageKey, Object.values(data as Record<string, unknown>));
    } else {
      storage.replaceAll(storageKey, data as unknown[]);
    }
  };

  // ── Record-specific helpers ───────────────────────────────────────

  const recordCreateDeleteMessage = recInput?.deleteType
    ? (key: string, deletedTimeISO?: string) =>
        ({
          type: recInput.deleteType,
          key,
          deletedTimeISO: deletedTimeISO ?? new Date().toISOString(),
        }) as unknown as SyncClientMessage
    : undefined;

  const recordCreateDeleteBroadcastMessage = recInput?.deleteBroadcastType
    ? (key: string, deletedTimeISO?: string) =>
        ({
          type: recInput.deleteBroadcastType,
          key,
          deletedTimeISO: deletedTimeISO ?? new Date().toISOString(),
        }) as unknown as SyncServerMessage
    : undefined;

  // For record entities, the push message carries { key, entry, updatedAtISO }
  // Override createPushMessage and extractFromMessage for record entities
  const recordCreatePushMessage = isRecord
    ? (key: string, entry: unknown, updatedAtISO: string) =>
        ({
          type: messages.push.type,
          key,
          entry,
          updatedAtISO,
        }) as unknown as SyncClientMessage
    : undefined;

  const recordCreateBroadcastMessage = isRecord
    ? (key: string, entry: unknown, updatedAtISO: string) =>
        ({
          type: messages.broadcast.type,
          key,
          entry,
          updatedAtISO,
        }) as unknown as SyncServerMessage
    : undefined;

  // ── Group-side broadcast helpers (mirror: 'split' record entities) ──
  //
  // Same shape as the user-side broadcasts but with the SG wire types
  // (`sg-deckProperties-updated`, etc.). Built only when the entity is
  // a split record AND the group overrides are present — the
  // mirror: 'split' invariants enforced earlier guarantee they are.
  // `isSplitRecord` is declared near the top of the factory.
  const recordCreateGroupBroadcastMessage =
    isSplitRecord && input.groupMessages?.broadcast.type
      ? (key: string, entry: unknown, updatedAtISO: string) =>
          ({
            type: input.groupMessages!.broadcast.type,
            key,
            entry,
            updatedAtISO,
          }) as unknown as SyncServerMessage
      : undefined;

  const recordCreateGroupDeleteBroadcastMessage =
    isSplitRecord && input.groupDeleteBroadcastType
      ? (key: string, deletedTimeISO?: string) =>
          ({
            type: input.groupDeleteBroadcastType,
            key,
            deletedTimeISO: deletedTimeISO ?? new Date().toISOString(),
          }) as unknown as SyncServerMessage
      : undefined;

  // ── Bulk wire derivation (record entities only) ────────────────────
  //
  // Naming: `${name}-push` → `${name}-bulk-push`. Derives from the
  // OVERRIDDEN message type (so quizProgress's 'quiz-push' becomes
  // 'quiz-bulk-push', not 'quizProgress-bulk-push'). Identity for
  // non-record entities — bulk machinery is meaningless there.
  const stripPush = (t: string): string => t.replace(/-push$/, '');
  const stripUpdated = (t: string): string => t.replace(/-updated$/, '');
  const stripDelete = (t: string): string => t.replace(/-delete$/, '');
  const stripDeleted = (t: string): string => t.replace(/-deleted$/, '');

  const bulkPushType = isRecord
    ? `${stripPush(messages.push.type)}-bulk-push`
    : undefined;
  const bulkBroadcastType = isRecord
    ? `${stripUpdated(messages.broadcast.type)}-bulk-updated`
    : undefined;
  const bulkDeleteType =
    isRecord && recInput?.deleteType
      ? `${stripDelete(recInput.deleteType)}-bulk-delete`
      : undefined;
  const bulkDeleteBroadcastType =
    isRecord && recInput?.deleteBroadcastType
      ? `${stripDeleted(recInput.deleteBroadcastType)}-bulk-deleted`
      : undefined;

  // Group-side bulk wires for `mirror: 'split'` records.
  const groupBulkPushType =
    isSplitRecord && input.groupMessages?.push.type
      ? `${stripPush(input.groupMessages.push.type)}-bulk-push`
      : undefined;
  const groupBulkBroadcastType =
    isSplitRecord && input.groupMessages?.broadcast.type
      ? `${stripUpdated(input.groupMessages.broadcast.type)}-bulk-updated`
      : undefined;
  const groupBulkDeleteType =
    isSplitRecord && input.groupDeleteType
      ? `${stripDelete(input.groupDeleteType)}-bulk-delete`
      : undefined;
  const groupBulkDeleteBroadcastType =
    isSplitRecord && input.groupDeleteBroadcastType
      ? `${stripDeleted(input.groupDeleteBroadcastType)}-bulk-deleted`
      : undefined;

  /** Max entries per bulk envelope before chunking. Matches the
   *  validator's `.max(...)` so the wire can never carry more than
   *  this in a single frame. */
  const BULK_CHUNK_SIZE = 100;
  const BULK_MAX_ENTRIES = 500;

  // Factories for bulk messages.
  const createBulkPushMessage = isRecord
    ? (
        entries: Array<{ key: string; entry: unknown; updatedAtISO: string }>,
        maxTs: string,
      ) =>
        ({
          type: bulkPushType,
          entries,
          updatedAtISO: maxTs,
        }) as unknown as SyncClientMessage
    : undefined;

  const createBulkDeleteMessage =
    isRecord && bulkDeleteType
      ? (keys: Array<{ key: string; deletedTimeISO: string }>, maxTs: string) =>
          ({
            type: bulkDeleteType,
            keys,
            updatedAtISO: maxTs,
          }) as unknown as SyncClientMessage
      : undefined;

  const createBulkBroadcastMessage = isRecord
    ? (
        entries: Array<{ key: string; entry: unknown; updatedAtISO: string }>,
        maxTs: string,
      ) =>
        ({
          type: bulkBroadcastType,
          entries,
          updatedAtISO: maxTs,
        }) as unknown as SyncServerMessage
    : undefined;

  const createBulkDeleteBroadcastMessage =
    isRecord && bulkDeleteBroadcastType
      ? (keys: Array<{ key: string; deletedTimeISO: string }>, maxTs: string) =>
          ({
            type: bulkDeleteBroadcastType,
            keys,
            updatedAtISO: maxTs,
          }) as unknown as SyncServerMessage
      : undefined;

  const createGroupBulkBroadcastMessage =
    isSplitRecord && groupBulkBroadcastType
      ? (
          entries: Array<{ key: string; entry: unknown; updatedAtISO: string }>,
          maxTs: string,
        ) =>
          ({
            type: groupBulkBroadcastType,
            entries,
            updatedAtISO: maxTs,
          }) as unknown as SyncServerMessage
      : undefined;

  const createGroupBulkDeleteBroadcastMessage =
    isSplitRecord && groupBulkDeleteBroadcastType
      ? (keys: Array<{ key: string; deletedTimeISO: string }>, maxTs: string) =>
          ({
            type: groupBulkDeleteBroadcastType,
            keys,
            updatedAtISO: maxTs,
          }) as unknown as SyncServerMessage
      : undefined;

  // ── Compose inbound message validators from the entity's Zod schema ──
  //
  // Built once at define-time so the hot path just does `validator.safeParse(msg)`.
  // Shape of the push envelope varies by entity kind:
  //   - array / collection:  { type, [dataKey]: T[], updatedAtISO }
  //   - value:               { type, [dataKey]: T,   updatedAtISO }
  //   - record:              { type, key, entry: T,  updatedAtISO }
  // Delete envelopes (record only): { type, key }.
  const validationSchema = input.validationSchema as z.ZodTypeAny | undefined;
  let pushMessageValidator: z.ZodTypeAny | undefined;
  let deleteMessageValidator: z.ZodTypeAny | undefined;
  // Group-side validators for `mirror: 'split'` entities. Built only
  // when the entity is a split record (Phase C slice 4a scope) — their
  // `type` literal is the SG-side push/delete type so an envelope
  // arriving on the StudyGroupDo socket validates correctly.
  let groupPushMessageValidator: z.ZodTypeAny | undefined;
  let groupDeleteMessageValidator: z.ZodTypeAny | undefined;
  // Bulk validators (record entities only). The bulk envelope shape:
  //   { type, entries: Array<{key, entry, updatedAtISO}>, updatedAtISO }
  // The entry validator inside the array reuses the existing
  // per-entity `validationSchema`. `key` enforces no-control-chars
  // (no `\x00` / `\x01` for the leading byte — the framework needs to
  // segment by `\x01` for compound keys like `deckId\x01cardId`, so we
  // forbid leading delimiters but allow internal ones).
  let bulkPushMessageValidator: z.ZodTypeAny | undefined;
  let bulkDeleteMessageValidator: z.ZodTypeAny | undefined;
  let groupBulkPushMessageValidator: z.ZodTypeAny | undefined;
  let groupBulkDeleteMessageValidator: z.ZodTypeAny | undefined;

  const safeKeySchema = z
    .string()
    .min(1)
    // eslint-disable-next-line no-control-regex
    .regex(/^[^\x00\x01]/u, 'key must not start with the storage delimiter');
  if (validationSchema) {
    if (isRecord) {
      pushMessageValidator = z
        .object({
          type: z.literal(messages.push.type),
          key: z.string().min(1),
          entry: validationSchema,
          updatedAtISO: z.string().optional(),
        })
        .passthrough();
      if (recInput?.deleteType) {
        deleteMessageValidator = z
          .object({
            type: z.literal(recInput.deleteType),
            key: z.string().min(1),
            // Optional for backwards compatibility — older clients
            // don't send it. `handleDelete` falls back to a `now()`
            // tombstone when absent.
            deletedTimeISO: z.string().optional(),
          })
          .passthrough();
      }
      if (isSplitRecord && input.groupMessages?.push.type) {
        groupPushMessageValidator = z
          .object({
            type: z.literal(input.groupMessages.push.type),
            key: z.string().min(1),
            entry: validationSchema,
            updatedAtISO: z.string().optional(),
          })
          .passthrough();
      }
      if (isSplitRecord && input.groupDeleteType) {
        groupDeleteMessageValidator = z
          .object({
            type: z.literal(input.groupDeleteType),
            key: z.string().min(1),
            deletedTimeISO: z.string().optional(),
          })
          .passthrough();
      }
      // ── Bulk validators (record entities) ────────────────────────
      if (bulkPushType) {
        bulkPushMessageValidator = z
          .object({
            type: z.literal(bulkPushType),
            entries: z
              .array(
                z
                  .object({
                    key: safeKeySchema,
                    entry: validationSchema,
                    updatedAtISO: z.string().optional(),
                  })
                  .passthrough(),
              )
              .min(1)
              .max(BULK_MAX_ENTRIES),
            updatedAtISO: z.string().optional(),
          })
          .passthrough();
      }
      if (bulkDeleteType) {
        bulkDeleteMessageValidator = z
          .object({
            type: z.literal(bulkDeleteType),
            keys: z
              .array(
                z
                  .object({
                    key: safeKeySchema,
                    deletedTimeISO: z.string().optional(),
                  })
                  .passthrough(),
              )
              .min(1)
              .max(BULK_MAX_ENTRIES),
            updatedAtISO: z.string().optional(),
          })
          .passthrough();
      }
      if (isSplitRecord && groupBulkPushType) {
        groupBulkPushMessageValidator = z
          .object({
            type: z.literal(groupBulkPushType),
            entries: z
              .array(
                z
                  .object({
                    key: safeKeySchema,
                    entry: validationSchema,
                    updatedAtISO: z.string().optional(),
                  })
                  .passthrough(),
              )
              .min(1)
              .max(BULK_MAX_ENTRIES),
            updatedAtISO: z.string().optional(),
          })
          .passthrough();
      }
      if (isSplitRecord && groupBulkDeleteType) {
        groupBulkDeleteMessageValidator = z
          .object({
            type: z.literal(groupBulkDeleteType),
            keys: z
              .array(
                z
                  .object({
                    key: safeKeySchema,
                    deletedTimeISO: z.string().optional(),
                  })
                  .passthrough(),
              )
              .min(1)
              .max(BULK_MAX_ENTRIES),
            updatedAtISO: z.string().optional(),
          })
          .passthrough();
      }
    } else {
      const payloadSchema =
        input.type === 'array' || input.type === 'collection'
          ? z.array(validationSchema)
          : validationSchema;
      pushMessageValidator = z
        .object({
          type: z.literal(messages.push.type),
          [pushDataKey]: payloadSchema,
          updatedAtISO: z.string().optional(),
        })
        .passthrough();
    }
  }

  // Phase C invariant: `mirror: 'split'` is only meaningful when both
  // authorities host the row, and requires a complete `fieldScopes`
  // map plus per-authority storage/messages overrides so the framework
  // knows where to land the SG-side rows and which wire types to
  // emit. Fail loudly at construction time so a half-configured entity
  // can't ship.
  if (resolvedMirror === 'split') {
    if (resolvedAuthority !== 'both') {
      throw new Error(
        `[defineSyncEntity:${name}] mirror: 'split' requires authority: 'both' (got '${resolvedAuthority}')`,
      );
    }
    if (!input.fieldScopes) {
      throw new Error(
        `[defineSyncEntity:${name}] mirror: 'split' requires fieldScopes — the projection cannot route fields without a scope map`,
      );
    }
    if (!input.groupStorageKey) {
      throw new Error(
        `[defineSyncEntity:${name}] mirror: 'split' requires groupStorageKey — the StudyGroupDo side needs a distinct storage prefix from the UserDo side`,
      );
    }
    if (!input.groupMessages) {
      throw new Error(
        `[defineSyncEntity:${name}] mirror: 'split' requires groupMessages — the StudyGroupDo side needs distinct wire message types`,
      );
    }
    if (isRecord && recInput?.deleteType && !input.groupDeleteType) {
      throw new Error(
        `[defineSyncEntity:${name}] mirror: 'split' record entity declares deleteType '${recInput.deleteType}' but no groupDeleteType — the SG side needs a parallel delete message name`,
      );
    }
  }

  // Compute the per-authority overrides for split entities. Empty for
  // everything else — registry builders fall back to the top-level
  // `storageKey`/`pushType`/etc. when the group-side override is
  // absent.
  const groupStorageKey: string | undefined = input.groupStorageKey;
  const groupTsKey: string | undefined = groupStorageKey
    ? `__${groupStorageKey}`
    : undefined;
  const groupPushType: string | undefined = input.groupMessages?.push.type;
  const groupBroadcastType: string | undefined = input.groupMessages?.broadcast.type;
  const groupPushDataKey: string | undefined = input.groupMessages?.push.dataKey;
  const groupBroadcastDataKey: string | undefined =
    input.groupMessages?.broadcast.dataKey;
  const groupDoPrefix: string | undefined =
    isRecord && groupStorageKey ? `${groupStorageKey}${KEY_DELIM}` : undefined;
  const groupTsKeyPrefix: string | undefined =
    isRecord && groupStorageKey ? `__${groupStorageKey}${KEY_DELIM}` : undefined;
  const groupDeleteType: string | undefined = input.groupDeleteType;
  const groupDeleteBroadcastType: string | undefined = input.groupDeleteBroadcastType;
  // Silence unused-var warnings — these data keys are reserved for the
  // future split-aware push/broadcast factories that Phase C slice 3
  // wires up. Phase C slice 2 just exposes the override declarations;
  // the factories that consume them ship in the next slice.
  void groupPushDataKey;
  void groupBroadcastDataKey;

  return {
    name,
    authority: resolvedAuthority,
    mirror: resolvedMirror,
    stateKey,
    tsKey,
    diffKey,
    storageKey,
    type: input.type as SyncEntity<T>['type'],
    primaryKey:
      input.type === 'array' || input.type === 'collection'
        ? input.primaryKey
        : undefined,
    indexById:
      input.type === 'collection'
        ? (input as CollectionEntityInput).indexById
        : undefined,
    initial: input.initial as T,
    input,

    // Generic primitives forwarded from input
    nested: input.nested,
    parentKeyField: nestedRecord ? input.storage?.pk?.[0] : undefined,
    fieldScopes: input.fieldScopes,
    authorize: input.authorize,

    // Record-specific fields
    entryKey: recInput?.entryKey,
    doPrefix: recInput?.doPrefix,
    tsKeyPrefix: recInput?.tsKeyPrefix,
    deleteType: recInput?.deleteType,
    deleteBroadcastType: recInput?.deleteBroadcastType,
    serverKeysField: recInput?.serverKeysField,
    normalizeServerEntry: recInput?.normalizeServerEntry,
    mergeEntry: recInput?.mergeEntry as
      | ((base: LWWItem, incoming: LWWItem) => LWWItem)
      | undefined,
    foldBroadcasts: recInput?.foldBroadcasts === true,
    decomposeStorageKey: recInput?.decomposeStorageKey,

    createPushMessage,
    createBroadcastMessage,
    extractFromMessage,
    pushType: messages.push.type,
    broadcastType: messages.broadcast.type,

    // Per-authority overrides (undefined for non-split entities). The
    // registry builders use these to populate the SG-side dispatch
    // maps with the right keys.
    groupStorageKey,
    groupTsKey,
    groupPushType,
    groupBroadcastType,
    groupDoPrefix,
    groupTsKeyPrefix,
    groupDeleteType,
    groupDeleteBroadcastType,

    // Bulk wire types (record entities only — undefined elsewhere).
    bulkPushType,
    bulkBroadcastType,
    bulkDeleteType,
    bulkDeleteBroadcastType,
    groupBulkPushType,
    groupBulkBroadcastType,
    groupBulkDeleteType,
    groupBulkDeleteBroadcastType,

    pushMessageValidator,
    deleteMessageValidator,
    groupPushMessageValidator,
    groupDeleteMessageValidator,
    bulkPushMessageValidator,
    bulkDeleteMessageValidator,
    groupBulkPushMessageValidator,
    groupBulkDeleteMessageValidator,

    // ── Server: handle push ──────────────────────────────────────────

    async handlePush(
      doStorage: DOStorage,
      broadcast: (msg: SyncServerMessage) => void,
      msg: unknown,
    ) {
      const m = msg as SyncMsgEnvelope;

      if (isRecord) {
        // Record: per-entry push with { key, entry, updatedAtISO }
        const key = m.key as string;
        const incoming = m.entry as LWWItem;
        const doKey = recInput!.doPrefix + key;
        const existing = (await doStorage.get(doKey)) as LWWItem | undefined;

        let merged: LWWItem = incoming;
        if (existing && recInput!.mergeEntry) {
          merged = recInput!.mergeEntry(existing, incoming);
          // A push that adds nothing (a stale reconnect flush, a re-push of
          // a row the server already merged) is a no-op: no write, and no
          // re-broadcast of the stored row — a peer with its own newer
          // write in flight would see that write flicker back until its
          // next diff.
          if (sameEntryContent(merged, existing)) return;
          merged = stampMergedAfterInputs(recInput!, existing, incoming, merged);
        } else if (existing) {
          // Default LWW by updatedAtISO. Ties go to incoming — matches
          // `isNewerDocument` / `mergeCardsLWW` / `mergeItemsLWW` so a
          // simultaneous re-push at the same ms isn't silently dropped.
          if ((existing.updatedAtISO ?? '') > (incoming.updatedAtISO ?? '')) {
            return; // existing is strictly newer, skip
          }
        }

        // Heal-on-write: a row must never reach storage without an
        // `updatedAtISO`. Without it, `loadForDiffRecord`'s `!clientTs`
        // gate matches every sync-init's missing entry and the row gets
        // resent on every reconnect; the client then persists an
        // empty-string timestamp and the loop perpetuates itself
        // (EMPTY_TS_BUG_2026-05-19). Prefer the envelope's
        // `updatedAtISO`, fall back to `now()` so this is the LAST
        // chance for a row to acquire a timestamp.
        const stampedTs: string =
          merged.updatedAtISO ?? m.updatedAtISO ?? new Date().toISOString();
        if (!merged.updatedAtISO) {
          merged = { ...merged, updatedAtISO: stampedTs };
        }

        await doStorage.put(doKey, merged as StorageDoc);
        broadcast(recordCreateBroadcastMessage!(key, merged, stampedTs));
        return;
      }

      const incoming = extractFromMessage(m);

      if (input.type === 'collection') {
        // Per-item LWW: merge incoming items with existing collection
        const pk = (input as CollectionEntityInput).primaryKey;
        const existing = await doStorage.get(storageKey);
        const base = (
          existing ? ((existing[pushDataKey] as LWWItem[] | undefined) ?? []) : []
        ) as LWWItem[];
        const merged = mergeItemsLWW(base, incoming as LWWItem[], pk);
        // Use max(existing, incoming) for the doc-level `updatedAtISO` —
        // never rewind. An out-of-order push (e.g. two clients pushing
        // at T3 then T2) that overwrites with the older timestamp would
        // break the `loadForDiff` gate (which compares `server.updatedAtISO
        // > clientTs`) — clients would stop receiving diffs even though
        // newer items are present server-side.
        const incomingTs = m.updatedAtISO ?? '';
        const existingTs = (existing?.updatedAtISO as string | undefined) ?? '';
        const nextTs = incomingTs > existingTs ? incomingTs : existingTs;
        await doStorage.put(storageKey, {
          [pushDataKey]: merged,
          updatedAtISO: nextTs,
        });
        // Broadcast only the incoming (changed) items, not the full collection
        broadcast(createBroadcastMessage(incoming, incomingTs));
        return;
      }

      // Document-level LWW for array/value entities
      const incomingTs = m.updatedAtISO ?? '';
      const existing = await doStorage.get(storageKey);
      if (existing && !isNewerDocument(existing.updatedAtISO, incomingTs)) return;

      await doStorage.put(storageKey, {
        [pushDataKey]: incoming,
        updatedAtISO: incomingTs,
      });
      broadcast(createBroadcastMessage(incoming, incomingTs));
    },

    // ── Server: handle delete (record only) ─────────────────────────

    handleDelete: isRecord
      ? async (
          doStorage: DOStorage,
          broadcast: (msg: SyncServerMessage) => void,
          msg: unknown,
        ) => {
          const m = msg as SyncMsgEnvelope & { deletedTimeISO?: string };
          const key = m.key as string;
          const doKey = recInput!.doPrefix + key;
          const existing = (await doStorage.get(doKey)) as LWWItem | undefined;

          // LWW gate: reject stale deletes. Without this, a delete
          // emitted at T1 that races against a fresher push at T3 (e.g.
          // re-create after delete) would wipe the newer entry. The
          // wire envelope must carry `deletedTimeISO` — without it the
          // server has no way to compare and must conservatively skip
          // the delete entirely.
          const incomingTs = m.deletedTimeISO ?? '';
          if (existing && incomingTs) {
            const existingTs = (existing.updatedAtISO ?? '') as string;
            if (existingTs > incomingTs) {
              return; // stale delete — existing entry is strictly newer
            }
          } else if (existing && !incomingTs) {
            // Legacy delete with no timestamp on the wire — conservative
            // soft-delete by stamping `deletedTimeISO = now()`. Drops
            // the row only on the next sync round once the tombstone
            // has propagated.
            const now = new Date().toISOString();
            const tombstoned = {
              ...existing,
              deletedTimeISO: now,
              updatedAtISO: now,
            } as StorageDoc;
            await doStorage.put(doKey, tombstoned);
            if (recordCreateDeleteBroadcastMessage) {
              broadcast(recordCreateDeleteBroadcastMessage(key, now));
            }
            return;
          }

          // Soft-delete: keep the row with a tombstone marker so the
          // delete propagates to offline clients via loadForDiffRecord.
          // Hard-delete only after the tombstone has been seen by all
          // clients (out of scope here — a separate compaction pass).
          if (existing) {
            const tombstoned = {
              ...existing,
              deletedTimeISO: incomingTs,
              updatedAtISO: incomingTs,
            } as StorageDoc;
            await doStorage.put(doKey, tombstoned);
          }
          if (recordCreateDeleteBroadcastMessage) {
            broadcast(recordCreateDeleteBroadcastMessage(key, incomingTs));
          }
        }
      : undefined,

    // ── Server: handle group-side push (split record entities only) ──
    //
    // Mirrors `handlePush` for the StudyGroupDo authority. The two
    // differences from the user-side handlePush:
    //   1. Projects the incoming entry via `projectEntity(_, 'group',
    //      fieldScopes)` — strips personal-scope keys at the boundary.
    //      This is the load-bearing security invariant for the split.
    //   2. Reads/writes at `groupDoPrefix + key` (e.g.
    //      `sgdeckmeta\x01{id}`) and broadcasts via `groupBroadcastType`.
    //
    // The group-side registry-builder (`group-entity-registry.ts`)
    // re-exposes this as `entity.handlePush` for split entities so the
    // dispatch loop in `SyncYServer.onCustomMessage` continues to call
    // `entity.handlePush(...)` without knowing about the variant.

    handleGroupPush:
      isSplitRecord && groupDoPrefix && recordCreateGroupBroadcastMessage
        ? async (
            doStorage: DOStorage,
            broadcast: (msg: SyncServerMessage) => void,
            msg: unknown,
          ) => {
            const m = msg as SyncMsgEnvelope;
            const key = m.key as string;
            const rawIncoming = m.entry as LWWItem;
            // Project: strip personal-scope keys, keep identity + shared.
            const projected = projectEntity(
              rawIncoming as Record<string, unknown>,
              'group',
              input.fieldScopes!,
            ) as LWWItem;
            // Row-level `updatedAtISO` is the LWW gate for SG storage. If
            // the entity's `fieldScopes` classify it as `personal`, the
            // projection drops it — but the SG row STILL needs a row-
            // level ts. Restore from the wire envelope.
            const envelopeTs = (m.updatedAtISO ?? rawIncoming.updatedAtISO) as
              | string
              | undefined;
            const incoming: LWWItem = {
              ...projected,
              ...(envelopeTs !== undefined ? { updatedAtISO: envelopeTs } : {}),
            };

            const doKey = groupDoPrefix + key;
            const existing = (await doStorage.get(doKey)) as LWWItem | undefined;

            let merged: LWWItem = incoming;
            if (existing && recInput!.mergeEntry) {
              merged = recInput!.mergeEntry(existing, incoming);
              // Nothing new — no write, no re-broadcast (see handlePush).
              if (sameEntryContent(merged, existing)) return;
              merged = stampMergedAfterInputs(recInput!, existing, incoming, merged);
            } else if (existing) {
              // Default LWW by updatedAtISO. Ties go to incoming, matching
              // the user-side handlePush + sync-merge primitives.
              if ((existing.updatedAtISO ?? '') > (incoming.updatedAtISO ?? '')) {
                return; // stale push — existing entry strictly newer
              }
            }

            // Heal-on-write: SG rows must never reach storage without an
            // `updatedAtISO` for the same reason as the user-side path
            // (EMPTY_TS_BUG_2026-05-19). Projection can have stripped
            // `updatedAtISO` if `fieldScopes` classified it as personal —
            // but the row above already restored from the envelope when
            // possible, so this fallback is rare and safe.
            const stampedTs: string =
              merged.updatedAtISO ?? m.updatedAtISO ?? new Date().toISOString();
            if (!merged.updatedAtISO) {
              merged = { ...merged, updatedAtISO: stampedTs };
            }

            await doStorage.put(doKey, merged as StorageDoc);
            broadcast(recordCreateGroupBroadcastMessage(key, merged, stampedTs));
          }
        : undefined,

    // ── Server: handle group-side delete (split record entities only) ─
    //
    // Same LWW + tombstone semantics as `handleDelete`, but reads/writes
    // at `groupDoPrefix` and broadcasts via `groupDeleteBroadcastType`.

    handleGroupDelete:
      isSplitRecord && groupDoPrefix && recordCreateGroupDeleteBroadcastMessage
        ? async (
            doStorage: DOStorage,
            broadcast: (msg: SyncServerMessage) => void,
            msg: unknown,
          ) => {
            const m = msg as SyncMsgEnvelope & { deletedTimeISO?: string };
            const key = m.key as string;
            const doKey = groupDoPrefix + key;
            const existing = (await doStorage.get(doKey)) as LWWItem | undefined;

            const incomingTs = m.deletedTimeISO ?? '';
            if (existing && incomingTs) {
              const existingTs = (existing.updatedAtISO ?? '') as string;
              if (existingTs > incomingTs) {
                return; // stale delete — existing entry strictly newer
              }
            } else if (existing && !incomingTs) {
              // Legacy delete without a timestamp — conservative tombstone
              // stamped with now(). Mirrors `handleDelete`'s fallback.
              const now = new Date().toISOString();
              const tombstoned = {
                ...existing,
                deletedTimeISO: now,
                updatedAtISO: now,
              } as StorageDoc;
              await doStorage.put(doKey, tombstoned);
              broadcast(recordCreateGroupDeleteBroadcastMessage(key, now));
              return;
            }

            if (existing) {
              const tombstoned = {
                ...existing,
                deletedTimeISO: incomingTs,
                updatedAtISO: incomingTs,
              } as StorageDoc;
              await doStorage.put(doKey, tombstoned);
            }
            broadcast(recordCreateGroupDeleteBroadcastMessage(key, incomingTs));
          }
        : undefined,

    // ── Server: handle bulk push (record entities) ─────────────────
    //
    // Loops `msg.entries`. For each entry: applies the same LWW gate
    // as the per-entry `handlePush`. Batches reads + writes so the
    // dispatch is O(1) storage roundtrips instead of O(N). Emits ONE
    // bulk-updated broadcast carrying only the accepted entries — peers
    // that don't yet understand the bulk wire can fall back to per-entry
    // broadcasts via the legacy path; new clients consume the bulk frame
    // directly.

    handleBulkPush:
      isRecord && createBulkBroadcastMessage
        ? async (
            doStorage: DOStorage,
            broadcast: (msg: SyncServerMessage) => void,
            msg: unknown,
          ) => {
            const m = msg as {
              entries?: Array<{ key: string; entry: LWWItem; updatedAtISO?: string }>;
            };
            const entries = m.entries ?? [];
            if (entries.length === 0) return;
            const prefix = recInput!.doPrefix;
            const doKeys = entries.map((e) => prefix + e.key);
            const existingMap = (await doStorage.get(doKeys)) as Map<
              string,
              LWWItem | undefined
            >;
            const writes: Record<string, StorageDoc> = {};
            const accepted: Array<{ key: string; entry: LWWItem; updatedAtISO: string }> =
              [];
            let maxTs = '';
            for (const {
              key,
              entry: incoming,
              updatedAtISO: entryEnvelopeTs,
            } of entries) {
              const doKey = prefix + key;
              const existing = existingMap.get(doKey);
              let merged: LWWItem = incoming;
              if (existing && recInput!.mergeEntry) {
                merged = recInput!.mergeEntry(existing, incoming);
                // Nothing new — not written, not broadcast (see handlePush).
                if (sameEntryContent(merged, existing)) continue;
                merged = stampMergedAfterInputs(recInput!, existing, incoming, merged);
              } else if (existing) {
                // Default LWW. Ties go to incoming.
                if ((existing.updatedAtISO ?? '') > (incoming.updatedAtISO ?? '')) {
                  continue;
                }
              }
              writes[doKey] = merged as StorageDoc;
              const acceptedTs =
                merged.updatedAtISO ?? incoming.updatedAtISO ?? entryEnvelopeTs ?? '';
              accepted.push({ key, entry: merged, updatedAtISO: acceptedTs });
              if (acceptedTs > maxTs) maxTs = acceptedTs;
            }
            if (accepted.length === 0) return;
            await doStorage.put(writes);
            broadcast(createBulkBroadcastMessage(accepted, maxTs));
          }
        : undefined,

    // ── Server: handle bulk delete (record entities) ───────────────

    handleBulkDelete:
      isRecord && createBulkDeleteBroadcastMessage
        ? async (
            doStorage: DOStorage,
            broadcast: (msg: SyncServerMessage) => void,
            msg: unknown,
          ) => {
            const m = msg as {
              keys?: Array<{ key: string; deletedTimeISO?: string }>;
            };
            const keys = m.keys ?? [];
            if (keys.length === 0) return;
            const prefix = recInput!.doPrefix;
            const doKeys = keys.map((k) => prefix + k.key);
            const existingMap = (await doStorage.get(doKeys)) as Map<
              string,
              LWWItem | undefined
            >;
            const writes: Record<string, StorageDoc> = {};
            const tombstoned: Array<{ key: string; deletedTimeISO: string }> = [];
            let maxTs = '';
            for (const { key, deletedTimeISO } of keys) {
              const doKey = prefix + key;
              const existing = existingMap.get(doKey);
              const incomingTs = deletedTimeISO ?? '';
              if (existing && incomingTs) {
                const existingTs = (existing.updatedAtISO ?? '') as string;
                if (existingTs > incomingTs) continue; // stale delete
              }
              const stampTs = incomingTs || new Date().toISOString();
              if (existing) {
                writes[doKey] = {
                  ...existing,
                  deletedTimeISO: stampTs,
                  updatedAtISO: stampTs,
                } as StorageDoc;
              }
              tombstoned.push({ key, deletedTimeISO: stampTs });
              if (stampTs > maxTs) maxTs = stampTs;
            }
            if (tombstoned.length === 0) return;
            if (Object.keys(writes).length > 0) await doStorage.put(writes);
            broadcast(createBulkDeleteBroadcastMessage(tombstoned, maxTs));
          }
        : undefined,

    // ── Server: handle group-side bulk push (split records) ────────

    handleGroupBulkPush:
      isSplitRecord && groupDoPrefix && createGroupBulkBroadcastMessage
        ? async (
            doStorage: DOStorage,
            broadcast: (msg: SyncServerMessage) => void,
            msg: unknown,
          ) => {
            const m = msg as {
              entries?: Array<{ key: string; entry: LWWItem; updatedAtISO?: string }>;
            };
            const entries = m.entries ?? [];
            if (entries.length === 0) return;
            const doKeys = entries.map((e) => groupDoPrefix + e.key);
            const existingMap = (await doStorage.get(doKeys)) as Map<
              string,
              LWWItem | undefined
            >;
            const writes: Record<string, StorageDoc> = {};
            const accepted: Array<{ key: string; entry: LWWItem; updatedAtISO: string }> =
              [];
            let maxTs = '';
            for (const {
              key,
              entry: rawIncoming,
              updatedAtISO: entryEnvelopeTs,
            } of entries) {
              // Project: strip personal-scope keys.
              const projected = projectEntity(
                rawIncoming as Record<string, unknown>,
                'group',
                input.fieldScopes!,
              ) as LWWItem;
              const envelopeTs = (entryEnvelopeTs ?? rawIncoming.updatedAtISO) as
                | string
                | undefined;
              const incoming: LWWItem = {
                ...projected,
                ...(envelopeTs !== undefined ? { updatedAtISO: envelopeTs } : {}),
              };

              const doKey = groupDoPrefix + key;
              const existing = existingMap.get(doKey);
              let merged: LWWItem = incoming;
              if (existing && recInput!.mergeEntry) {
                merged = recInput!.mergeEntry(existing, incoming);
                // Nothing new — not written, not broadcast (see handlePush).
                if (sameEntryContent(merged, existing)) continue;
                merged = stampMergedAfterInputs(recInput!, existing, incoming, merged);
              } else if (existing) {
                if ((existing.updatedAtISO ?? '') > (incoming.updatedAtISO ?? '')) {
                  continue;
                }
              }
              writes[doKey] = merged as StorageDoc;
              const acceptedTs = merged.updatedAtISO ?? envelopeTs ?? '';
              accepted.push({ key, entry: merged, updatedAtISO: acceptedTs });
              if (acceptedTs > maxTs) maxTs = acceptedTs;
            }
            if (accepted.length === 0) return;
            await doStorage.put(writes);
            broadcast(createGroupBulkBroadcastMessage(accepted, maxTs));
          }
        : undefined,

    // ── Server: handle group-side bulk delete (split records) ──────

    handleGroupBulkDelete:
      isSplitRecord && groupDoPrefix && createGroupBulkDeleteBroadcastMessage
        ? async (
            doStorage: DOStorage,
            broadcast: (msg: SyncServerMessage) => void,
            msg: unknown,
          ) => {
            const m = msg as {
              keys?: Array<{ key: string; deletedTimeISO?: string }>;
            };
            const keys = m.keys ?? [];
            if (keys.length === 0) return;
            const doKeys = keys.map((k) => groupDoPrefix + k.key);
            const existingMap = (await doStorage.get(doKeys)) as Map<
              string,
              LWWItem | undefined
            >;
            const writes: Record<string, StorageDoc> = {};
            const tombstoned: Array<{ key: string; deletedTimeISO: string }> = [];
            let maxTs = '';
            for (const { key, deletedTimeISO } of keys) {
              const doKey = groupDoPrefix + key;
              const existing = existingMap.get(doKey);
              const incomingTs = deletedTimeISO ?? '';
              if (existing && incomingTs) {
                const existingTs = (existing.updatedAtISO ?? '') as string;
                if (existingTs > incomingTs) continue;
              }
              const stampTs = incomingTs || new Date().toISOString();
              if (existing) {
                writes[doKey] = {
                  ...existing,
                  deletedTimeISO: stampTs,
                  updatedAtISO: stampTs,
                } as StorageDoc;
              }
              tombstoned.push({ key, deletedTimeISO: stampTs });
              if (stampTs > maxTs) maxTs = stampTs;
            }
            if (tombstoned.length === 0) return;
            if (Object.keys(writes).length > 0) await doStorage.put(writes);
            broadcast(createGroupBulkDeleteBroadcastMessage(tombstoned, maxTs));
          }
        : undefined,

    // ── Server: load for sync-diff ───────────────────────────────────

    async loadForDiff(doStorage: DOStorage, clientTs: string | undefined) {
      if (isRecord) {
        // Record entities use loadForDiffRecord instead
        return undefined;
      }
      const server = await doStorage.get(storageKey);
      if (server && (!clientTs || (server.updatedAtISO ?? '') > clientTs)) return server;
      return undefined;
    },

    // ── Server: load per-entry for sync-diff (record only) ──────────

    loadForDiffRecord: isRecord
      ? async (
          doStorage: DOStorage,
          keys: string[],
          clientTsMap: Record<string, string>,
        ) => {
          if (keys.length === 0) return undefined;
          const prefix = recInput!.doPrefix;
          let result: Record<string, unknown> | undefined;
          // Chunked get — a single multi-key get over the entire entity
          // materializes every body at once, which OOMs the 128 MB DO
          // isolate on large accounts (and 128 keys is the documented
          // per-get cap). Each chunk's Map is dropped before the next
          // fetch, so the TRANSIENT peak is one chunk. The accumulated
          // `result` is only bounded by how much is server-newer: small
          // for a returning client's manifest, but a cold start (no
          // client timestamps) still accumulates every body — bounding
          // THAT needs a streamed reply (see splitCardsForStreaming),
          // not a smaller chunk size.
          for (let i = 0; i < keys.length; i += DIFF_GET_CHUNK) {
            const chunk = keys.slice(i, i + DIFF_GET_CHUNK);
            const entries = await doStorage.get(chunk.map((k) => prefix + k));
            for (const [storageKey, entry] of entries) {
              if (!entry) continue;
              // Tombstones must be propagated — otherwise an offline client
              // that had the entry locally never learns about the delete,
              // either keeps showing the deleted entry or re-pushes it
              // on reconnect (resurrection).
              const key = storageKey.slice(prefix.length);
              const clientTs = clientTsMap[key];
              if (!clientTs || (entry.updatedAtISO ?? '') > clientTs) {
                if (!result) result = {};
                result[key] = entry;
              }
            }
          }
          return result;
        }
      : undefined,

    // ── Server: load per-entry for SG-side sync-diff (split records) ─
    //
    // Same logic as loadForDiffRecord but reads rows from
    // `groupDoPrefix` (e.g. `sgdeckmeta\x01`). The SG sync-init
    // builder swaps this in for split entities via the registry's
    // `adaptForGroup` wrapping.

    loadForDiffGroupRecord:
      isSplitRecord && groupDoPrefix
        ? async (
            doStorage: DOStorage,
            keys: string[],
            clientTsMap: Record<string, string>,
          ) => {
            if (keys.length === 0) return undefined;
            const prefix = groupDoPrefix;
            let result: Record<string, unknown> | undefined;
            // Chunked for the same reason as loadForDiffRecord above.
            for (let i = 0; i < keys.length; i += DIFF_GET_CHUNK) {
              const chunk = keys.slice(i, i + DIFF_GET_CHUNK);
              const entries = await doStorage.get(chunk.map((k) => prefix + k));
              for (const [storageKey, entry] of entries) {
                if (!entry) continue;
                const key = storageKey.slice(prefix.length);
                const clientTs = clientTsMap[key];
                if (!clientTs || (entry.updatedAtISO ?? '') > clientTs) {
                  if (!result) result = {};
                  result[key] = entry;
                }
              }
            }
            return result;
          }
        : undefined,

    // Record message factories
    createDeleteMessage: recordCreateDeleteMessage,
    createDeleteBroadcastMessage: recordCreateDeleteBroadcastMessage,

    // ── Sync engine: apply server diff ───────────────────────────────

    applySyncDiff(diff: unknown, ts: Record<string, string>, localData?: unknown) {
      const d = diff as Record<string, unknown>;

      if (isRecord) {
        // Record: diff carries Record<string, TEntry> at diffKey
        const serverEntries = d[diffKey] as Record<string, LWWItem> | undefined;
        if (!serverEntries) return undefined;

        // Server entries are keyed by the composite key; look each one up in
        // the local slice in place (a nested slice is NOT flattened first —
        // that was O(every resident card) per diff). `result` holds only the
        // diff's keys and is nested back at the boundary.
        const result: Record<string, LWWItem> = {};

        for (const [key, serverEntry] of Object.entries(serverEntries)) {
          const entryTsKey = recInput!.tsKeyPrefix + key;
          // Ingest boundary: repair-or-drop malformed server rows before
          // they can reach the store/persistence (see normalizeServerEntry).
          let se = serverEntry;
          if (recInput!.normalizeServerEntry) {
            const normalized = recInput!.normalizeServerEntry(key, serverEntry);
            if (normalized === null) continue; // unusable — don't apply, don't ts
            se = normalized;
          }
          const localEntry = lookupRecordEntry(localData, nestedRecord, key);

          if (localEntry && recInput!.mergeEntry) {
            const lastSyncedTs = ts[entryTsKey] ?? EPOCH;
            const localTs = localEntry.updatedAtISO ?? EPOCH;

            if (localTs > lastSyncedTs) {
              // Locally dirty — use custom merge
              const merged = recInput!.mergeEntry(se, localEntry);
              result[key] = merged;
              if (sameEntryContent(merged, se)) {
                // The server already holds everything the local row knew.
                // Fall back to EPOCH, not '', when neither side carries a
                // timestamp. An empty-string `clientTs` is truthy on the
                // server's `!clientTs` gate but compares as less-than every
                // real ISO timestamp, so the entry would be resent forever
                // (see EMPTY_TS_BUG_2026-05-19). EPOCH self-heals: the
                // server skips the entry when its own row also lacks
                // `updatedAtISO`.
                ts[entryTsKey] = merged.updatedAtISO ?? se.updatedAtISO ?? EPOCH;
              }
              // Otherwise the merge kept something the server lacks (an
              // offline note in the materials index, an attempt, a session
              // counter). The synced ts is left where it was so `flushLocal`
              // — which walks the ORIGINAL local rows — still sees this one
              // as dirty and pushes it; the server's own `mergeEntry` folds
              // it in and broadcasts the union. Recording the merged row's
              // ts here instead would mark the local row as already synced
              // and strand its half of the merge on this device for ever.
            } else {
              result[key] = se;
              ts[entryTsKey] = se.updatedAtISO ?? EPOCH;
            }
          } else {
            result[key] = se;
            ts[entryTsKey] = se.updatedAtISO ?? EPOCH;
          }
        }

        return (nestedRecord ? nestFlat(result) : result) as unknown as T;
      }

      const section = d[diffKey] as
        | { updatedAtISO?: string; [key: string]: unknown }
        | undefined;
      if (!section) return undefined;
      ts[tsKey] = section.updatedAtISO ?? EPOCH;
      const serverItems = section[pushDataKey];

      if (input.type === 'collection' && localData) {
        const pk = (input as CollectionEntityInput).primaryKey;
        const localArr = indexedCollection
          ? Object.values(localData as Record<string, LWWItem>)
          : (localData as LWWItem[]);
        const merged = mergeItemsLWW(localArr, serverItems as LWWItem[], pk);
        return (indexedCollection ? indexByPk(merged, pk) : merged) as unknown as T;
      }

      if (input.type === 'collection' && indexedCollection) {
        // No local data — index incoming server items by pk.
        const pk = (input as CollectionEntityInput).primaryKey;
        return indexByPk(serverItems as LWWItem[], pk) as unknown as T;
      }

      return serverItems as T;
    },

    // ── Sync engine: flush local data ────────────────────────────────

    flushLocal(
      diff: unknown,
      localData: unknown,
      ts: Record<string, string>,
      outbound: SyncClientMessage[],
    ) {
      // Server-authored: the client never pushes; the DO is the sole writer.
      if (serverAuthored) return;
      if (isRecord) {
        // Record: collect every dirty local entry into ONE bulk push frame
        // (chunked at BULK_CHUNK_SIZE), instead of emitting N per-entry
        // frames. Heal-on-touch stamps `entry.updatedAtISO` when missing
        // so EPOCH can never reach the wire (audit 2026-05-18 B-MED-10
        // + SYNC_FLOOD_2026-05-18.md).
        const d = diff as Record<string, unknown>;
        const serverKeysField =
          recInput!.serverKeysField ??
          `server${name.charAt(0).toUpperCase() + name.slice(1)}Keys`;
        const serverKeys = new Set<string>(
          (d[serverKeysField] as string[] | undefined) ?? [],
        );
        const dirty: Array<{ key: string; entry: LWWItem; updatedAtISO: string }> = [];
        // The reconnect flush genuinely needs every local entry (it is what
        // finds local-only rows), so this is the one O(resident) walk — done
        // in place rather than through a flattened copy.
        forEachRecordEntry(localData, nestedRecord, (key, entry) => {
          const entryTsKey = recInput!.tsKeyPrefix + key;
          // Heal-on-touch: never let EPOCH go out on the wire. Mirrors
          // detectDirty's behavior so flushLocal-path and live-mutation
          // path produce the same stamping shape. Prefer the entry's stable
          // `createdTimeISO` over `now()`: a timestamp-less row (e.g. an
          // SG-shared card whose projection omits `updatedAtISO`) must heal
          // to a DETERMINISTIC value, or a fresh `now()` each reconnect
          // out-ranks the persisted serverTs and the row re-pushes on every
          // load. Matches the hydrate-repair heal (flashka-store.ts) and
          // `cardTimestamp()` (sync-merge.ts).
          let localTs = entry.updatedAtISO;
          if (!localTs) {
            localTs =
              (entry as { createdTimeISO?: string }).createdTimeISO ||
              new Date().toISOString();
            entry.updatedAtISO = localTs;
          }
          const knownTs = ts[entryTsKey] ?? EPOCH;
          if (!serverKeys.has(key) || localTs > knownTs) {
            dirty.push({ key, entry, updatedAtISO: localTs });
            ts[entryTsKey] = localTs;
          }
        });
        if (dirty.length === 0) return;
        if (!createBulkPushMessage) {
          // Defensive fallback — should never happen for record entities
          // since the factory always builds the bulk wire.
          for (const { key, entry, updatedAtISO } of dirty) {
            outbound.push(recordCreatePushMessage!(key, entry, updatedAtISO));
          }
          return;
        }
        // Chunk at BULK_CHUNK_SIZE so a single envelope stays under
        // sync-compression's MAX_PLAIN_JSON_SIZE for typical card payloads.
        for (let i = 0; i < dirty.length; i += BULK_CHUNK_SIZE) {
          const slice = dirty.slice(i, i + BULK_CHUNK_SIZE);
          const maxTs = slice.reduce(
            (m, e) => (e.updatedAtISO > m ? e.updatedAtISO : m),
            '',
          );
          outbound.push(createBulkPushMessage(slice, maxTs));
        }
        return;
      }

      if (input.type === 'collection') {
        const localArr = indexedCollection
          ? (Object.values((localData ?? {}) as Record<string, LWWItem>) as LWWItem[])
          : ((localData ?? []) as LWWItem[]);
        if (localArr.length === 0) return;
        const pk = (input as CollectionEntityInput).primaryKey;

        const diffSection = (diff as Record<string, unknown>)[diffKey] as
          | { [key: string]: unknown }
          | undefined;
        const serverMap = new Map<string, string>();
        if (diffSection) {
          const items = (diffSection[pushDataKey] as LWWItem[] | undefined) ?? [];
          for (const item of items) {
            serverMap.set(item[pk] as string, item.updatedAtISO ?? '');
          }
        }

        // When the diff contains no section for this entity, the server is
        // silent — that means "up to date to the client's known ts", NOT
        // "server has nothing". Compare against `ts[tsKey]` (the last
        // successfully-synced collection timestamp) so we only push items
        // modified after that checkpoint. Without this, every refresh would
        // re-push the entire local collection (all items look "unknown to
        // server" because the server never mentioned them).
        const lastSyncedTs = ts[tsKey] ?? EPOCH;

        const fallbackTs = new Date().toISOString();
        const newer: LWWItem[] = [];
        for (const item of localArr) {
          // Items missing `updatedAtISO` would otherwise be silently
          // dropped. Stamp them with `now()` so they get pushed instead
          // of disappearing into a sync no-op (the engine MUST surface
          // legacy or freshly-created items, not pretend they don't
          // exist).
          let localItemTs = item.updatedAtISO ?? '';
          if (!localItemTs) {
            localItemTs = fallbackTs;
            item.updatedAtISO = fallbackTs;
          }
          const serverItemTs = serverMap.get(item[pk] as string);
          if (serverItemTs !== undefined) {
            // Server included this item — per-item LWW
            if (localItemTs > serverItemTs) newer.push(item);
          } else {
            // Server omitted this item — push only if modified after our
            // last sync checkpoint
            if (localItemTs > lastSyncedTs) newer.push(item);
          }
        }
        if (newer.length === 0) return;

        const maxTs =
          newer.reduce(
            (max: string, item: LWWItem) =>
              item.updatedAtISO && item.updatedAtISO > max ? item.updatedAtISO : max,
            '',
          ) || new Date().toISOString();
        outbound.push(createPushMessage(newer as unknown as T, maxTs));
        ts[tsKey] = maxTs;
        return;
      }

      if ((diff as Record<string, unknown>)[diffKey]) return;

      const isEmpty =
        input.type === 'array'
          ? !localData || (localData as unknown[]).length === 0
          : !localData;
      if (isEmpty) return;

      const localTs = ts[tsKey] ?? EPOCH;
      if (input.type === 'value' && localTs !== EPOCH) return;

      const fts = new Date().toISOString();
      outbound.push(createPushMessage(localData as T, fts));
      ts[tsKey] = fts;
    },

    // ── Sync engine: detect dirty ────────────────────────────────────

    detectDirty(
      prev: unknown,
      curr: unknown,
      ts: Record<string, string>,
      outbound: SyncClientMessage[],
      baseTs?: Readonly<Record<string, string>>,
    ) {
      // Server-authored: the client never pushes; the DO is the sole writer.
      if (serverAuthored) return;
      if (curr === prev) return;

      if (isRecord) {
        // Record: collect dirty + deleted entries into ONE bulk push +
        // ONE bulk delete frame each (chunked at BULK_CHUNK_SIZE),
        // instead of emitting N per-entry frames. Heal-on-touch stamps
        // missing `entry.updatedAtISO` so EPOCH never reaches the wire.
        //
        // Walked in place: a nested slice's untouched buckets are skipped by
        // identity, so a single card write is O(cards in that deck) — it used
        // to flatten BOTH snapshots (2 × every resident card) per write.
        const dirty: Array<{ key: string; entry: LWWItem; updatedAtISO: string }> = [];
        forEachChangedRecordEntry(prev, curr, nestedRecord, (key, entry) => {
          const entryTsKey = recInput!.tsKeyPrefix + key;
          // Heal-on-touch (see `flushLocal`): prefer the entry's stable
          // `createdTimeISO` over `now()` so a timestamp-less row converges
          // instead of re-pushing on every reconnect. Never EPOCH (B-MED-10).
          let entryTs = entry.updatedAtISO;
          if (!entryTs) {
            entryTs =
              (entry as { createdTimeISO?: string }).createdTimeISO ||
              new Date().toISOString();
            entry.updatedAtISO = entryTs;
          }
          // Read-through: a key this tick has not written yet comes from the
          // base map (`ts` is then only the delta — see the type's doc).
          const own = ts[entryTsKey];
          const serverTs = own !== undefined || !baseTs ? own : baseTs[entryTsKey];
          if (serverTs && entryTs <= serverTs) return;
          dirty.push({ key, entry, updatedAtISO: entryTs });
          ts[entryTsKey] = entryTs;
        });
        if (dirty.length > 0) {
          if (createBulkPushMessage) {
            for (let i = 0; i < dirty.length; i += BULK_CHUNK_SIZE) {
              const slice = dirty.slice(i, i + BULK_CHUNK_SIZE);
              const maxTs = slice.reduce(
                (m, e) => (e.updatedAtISO > m ? e.updatedAtISO : m),
                '',
              );
              outbound.push(createBulkPushMessage(slice, maxTs));
            }
          } else {
            for (const { key, entry, updatedAtISO } of dirty) {
              outbound.push(recordCreatePushMessage!(key, entry, updatedAtISO));
            }
          }
        }

        // Deleted entries (in prev but not in curr) → ONE bulk-delete frame.
        if (recordCreateDeleteMessage) {
          const deleted: Array<{ key: string; deletedTimeISO: string }> = [];
          forEachRemovedRecordKey(prev, curr, nestedRecord, (key) => {
            const deletedTimeISO = new Date().toISOString();
            deleted.push({ key, deletedTimeISO });
            const entryTsKey = recInput!.tsKeyPrefix + key;
            ts[entryTsKey] = deletedTimeISO;
          });
          if (deleted.length > 0) {
            if (createBulkDeleteMessage) {
              for (let i = 0; i < deleted.length; i += BULK_CHUNK_SIZE) {
                const slice = deleted.slice(i, i + BULK_CHUNK_SIZE);
                const maxTs = slice.reduce(
                  (m, e) => (e.deletedTimeISO > m ? e.deletedTimeISO : m),
                  '',
                );
                outbound.push(createBulkDeleteMessage(slice, maxTs));
              }
            } else {
              for (const { key, deletedTimeISO } of deleted) {
                outbound.push(recordCreateDeleteMessage(key, deletedTimeISO));
              }
            }
          }
        }
        return;
      }

      if (input.type === 'collection') {
        const pk = (input as CollectionEntityInput).primaryKey;
        const prevArr = indexedCollection
          ? (Object.values((prev ?? {}) as Record<string, LWWItem>) as LWWItem[])
          : ((prev ?? []) as LWWItem[]);
        const currArr = indexedCollection
          ? (Object.values((curr ?? {}) as Record<string, LWWItem>) as LWWItem[])
          : ((curr ?? []) as LWWItem[]);
        const prevMap = new Map<string, LWWItem>(
          prevArr.map((item) => [item[pk] as string, item]),
        );
        const changed: LWWItem[] = [];
        for (const item of currArr) {
          if (prevMap.get(item[pk] as string) !== item) {
            changed.push(item);
          }
        }
        if (changed.length === 0) return;
        const maxTs =
          changed.reduce(
            (max: string, item: LWWItem) =>
              item.updatedAtISO && item.updatedAtISO > max ? item.updatedAtISO : max,
            '',
          ) || new Date().toISOString();
        outbound.push(createPushMessage(changed as unknown as T, maxTs));
        ts[tsKey] = maxTs;
        return;
      }

      const updatedAtISO = new Date().toISOString();
      outbound.push(createPushMessage(curr as T, updatedAtISO));
      ts[tsKey] = updatedAtISO;
    },

    hydrate,
    persistReplace,
  };
}

// ---------------------------------------------------------------------------
// defineEntity — one-call entity declaration with required Zod schema.
//
// Both the UserDo (`sync-schema.ts`) and StudyGroupDo (`group-sync-schema.ts`)
// registries call this. Two near-identical wrappers existed for months,
// which drifted in subtle ways. This single source of truth takes an
// entity kind + payload schema and returns:
//   - `.T`       phantom type (extract via `typeof x.T`)
//   - `.schema`  Zod schema (for validation, re-export)
//   - `.entity`  SyncEntity for server handlers + client createClientEntity
//   - `.config`  literal-typed config for GenericEntityState derivation
//
// Overloads ensure TypeScript infers `Kind` as a literal (not the union).
// ---------------------------------------------------------------------------

type EntityKind = 'array' | 'collection' | 'value' | 'record';

// ── Wire message shapes (type-only, derived from entity config) ────────────
//
// Carry the literal `type`, `dataKey`, and payload type at the type level so
// the registry can union them into `SyncClientMessage` / `SyncServerMessage`
// automatically. No runtime cost — these types are projected onto phantom
// fields whose value is `undefined`.

/** Push message shape, discriminated by entity kind. */
export type PushMessageShape<
  Kind extends EntityKind,
  T,
  PushType extends string,
  PushDataKey extends string,
> = Kind extends 'record'
  ? { type: PushType; key: string; entry: T; updatedAtISO: string }
  : Kind extends 'value'
    ? { type: PushType; updatedAtISO: string } & { [K in PushDataKey]: T }
    : { type: PushType; updatedAtISO: string } & { [K in PushDataKey]: T[] };

/** Broadcast message shape — same as push, with the broadcast type/dataKey. */
export type BroadcastMessageShape<
  Kind extends EntityKind,
  T,
  BroadcastType extends string,
  BroadcastDataKey extends string,
> = Kind extends 'record'
  ? { type: BroadcastType; key: string; entry: T; updatedAtISO: string }
  : Kind extends 'value'
    ? { type: BroadcastType; updatedAtISO: string } & {
        [K in BroadcastDataKey]: T;
      }
    : { type: BroadcastType; updatedAtISO: string } & {
        [K in BroadcastDataKey]: T[];
      };

/** Delete message shape — record entities only. Carries `deletedTimeISO`
 * so the server's `handleDelete` LWW gate can reject stale deletes that
 * race against fresher re-creates. */
export type DeleteMessageShape<DeleteType extends string> = {
  type: DeleteType;
  key: string;
  deletedTimeISO: string;
};

/** Bulk push message shape — record entities only. Carries an
 *  `entries: Array<{key, entry, updatedAtISO}>` array; the framework
 *  loops with the same LWW gate as the per-entry path. */
export type BulkPushMessageShape<BulkPushType extends string, T> = {
  type: BulkPushType;
  entries: Array<{ key: string; entry: T; updatedAtISO: string }>;
  updatedAtISO: string;
};

/** Bulk broadcast shape — same payload, broadcast wire. */
export type BulkBroadcastMessageShape<BulkBroadcastType extends string, T> = {
  type: BulkBroadcastType;
  entries: Array<{ key: string; entry: T; updatedAtISO: string }>;
  updatedAtISO: string;
};

/** Bulk delete shape — record entities with a deleteType. */
export type BulkDeleteMessageShape<BulkDeleteType extends string> = {
  type: BulkDeleteType;
  keys: Array<{ key: string; deletedTimeISO: string }>;
  updatedAtISO: string;
};

type DefineEntityBase<
  T,
  Name extends string,
  Kind extends EntityKind,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
> = {
  name: Name;
  type: Kind;
  schema: z.ZodType<T>;
  initial?: Kind extends 'value' ? T : never;
  tsKey?: string;
  storageKey?: string;
  diffKey?: string;
  settingsKey?: string;
  /** Read-only on the client (see `BaseEntityInput.serverAuthored`). */
  serverAuthored?: boolean;
  messages?: {
    push: { type: PushType; dataKey: PushDataKey };
    broadcast: { type: BroadcastType; dataKey: BroadcastDataKey };
  };
  /** op-sqlite table layout (see `StorageInput`). Defaults auto-infer. */
  storage?: StorageInput;
  /** Record-only: state shape `Record<parentId, Record<childPart, T>>`
   *  (see `BaseEntityInput.nested`). */
  nested?: boolean;
  /** Per-field `shared`/`personal` scope map (see `BaseEntityInput.fieldScopes`). */
  fieldScopes?: Readonly<Record<keyof T & string, FieldScope>>;
  /** Authorization hook (see `BaseEntityInput.authorize`). */
  authorize?: AuthorizeHook;
  /**
   * Durable Object authority that hosts canonical state. See
   * `EntityAuthority`. Defaults to `'user'` when omitted.
   */
  authority?: EntityAuthority;
  /**
   * For `authority: 'both'` entities, how the two sides' rows relate.
   * See `EntityMirror`. Defaults to `'independent'`.
   */
  mirror?: EntityMirror;
  /**
   * SG-side storage prefix for `mirror: 'split'` entities. Required
   * when `mirror: 'split'`; ignored otherwise.
   */
  groupStorageKey?: string;
  /**
   * SG-side wire-message overrides for `mirror: 'split'` entities.
   * Required when `mirror: 'split'`; ignored otherwise. Plain strings
   * here (rather than literal generics) — the group-side wire names
   * are independent of the user-side `PushType`/`BroadcastType`
   * inference and don't participate in the same union derivation.
   */
  groupMessages?: {
    push: { type: string; dataKey: string };
    broadcast: { type: string; dataKey: string };
  };
  /**
   * SG-side delete-message overrides for `mirror: 'split'` record
   * entities. Required when the entity declares `deleteType`.
   */
  groupDeleteType?: string;
  groupDeleteBroadcastType?: string;
};

type DefineArrayOpts<
  T,
  Name extends string,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
> = DefineEntityBase<
  T,
  Name,
  'array',
  PushType,
  BroadcastType,
  PushDataKey,
  BroadcastDataKey
> & { primaryKey: string };

type DefineCollectionOpts<
  T,
  Name extends string,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
> = DefineEntityBase<
  T,
  Name,
  'collection',
  PushType,
  BroadcastType,
  PushDataKey,
  BroadcastDataKey
> & {
  primaryKey: string;
  /** State as `Record<primaryKey, T>` instead of `T[]` (see `CollectionEntityInput.indexById`). */
  indexById?: boolean;
};

type DefineValueOpts<
  T,
  Name extends string,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
> = DefineEntityBase<
  T,
  Name,
  'value',
  PushType,
  BroadcastType,
  PushDataKey,
  BroadcastDataKey
>;

type DefineRecordOpts<
  T,
  Name extends string,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
  DeleteType extends string | undefined = undefined,
  DeleteBroadcastType extends string | undefined = undefined,
> = DefineEntityBase<
  T,
  Name,
  'record',
  PushType,
  BroadcastType,
  PushDataKey,
  BroadcastDataKey
> & {
  entryKey: (entry: T) => string;
  doPrefix: string;
  tsKeyPrefix: string;
  mergeEntry?: (base: T, incoming: T) => T;
  /** Client-side broadcast fold — see `RecordEntityInput.foldBroadcasts`. */
  foldBroadcasts?: boolean;
  /** Ingest repair-or-drop hook — see `RecordEntityInput.normalizeServerEntry`. */
  normalizeServerEntry?: (key: string, entry: unknown) => T | null;
  serverKeysField?: string;
  deleteType?: DeleteType;
  deleteBroadcastType?: DeleteBroadcastType;
  decomposeStorageKey?: (key: string) => unknown;
};

/** Compute the stored state type from item type + Kind + shape flags.
 *
 * Tuple-wrapped conditionals here (and elsewhere in this file) so the
 * type stays correct under `strictNullChecks: false` consumers (today:
 * ts-jest). `[Nested] extends [true]` short-circuits to the nested shape
 * only when the literal `true` was supplied — under non-strict mode
 * `boolean extends true` would otherwise be true and every record entity
 * would land in the nested branch, not just `cards`. */
type DefineStateType<
  T,
  Kind extends EntityKind,
  Nested extends boolean = false,
  IndexById extends boolean = false,
> = [Kind] extends ['record']
  ? [Nested] extends [true]
    ? Record<string, Record<string, T>>
    : Record<string, T>
  : [Kind] extends ['array' | 'collection']
    ? [IndexById] extends [true]
      ? Record<string, T>
      : T[]
    : T;

export type DefineEntityResult<
  T,
  Name extends string,
  Kind extends EntityKind,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
  DeleteType extends string | undefined = undefined,
  DeleteBroadcastType extends string | undefined = undefined,
  Nested extends boolean = false,
  IndexById extends boolean = false,
> = {
  readonly config: {
    readonly name: Name;
    readonly type: Kind;
    readonly initial: DefineStateType<T, Kind, Nested, IndexById>;
    readonly primaryKey?: string;
    /** Record entities: true → state is `Record<parentId, Record<childPart, T>>`. */
    readonly nested?: Nested;
    /** Collection entities: true → state is `Record<primaryKey, T>` instead of `T[]`. */
    readonly indexById?: IndexById;
  };
  readonly entity: SyncEntity<DefineStateType<T, Kind, Nested, IndexById>>;
  /** Phantom — extract the item/value type via `typeof x.T`. */
  readonly T: T;
  readonly schema: z.ZodType<T>;
  /** Phantom carrier for the push wire message shape (undefined at runtime). */
  readonly PushMessage: PushMessageShape<Kind, T, PushType, PushDataKey>;
  /** Phantom carrier for the broadcast wire message shape. */
  readonly BroadcastMessage: BroadcastMessageShape<
    Kind,
    T,
    BroadcastType,
    BroadcastDataKey
  >;
  /** Phantom carrier for the delete push (record entities with `deleteType`).
   *
   * Tuple-wrapped checks guard against `strictNullChecks: false` consumers
   * (today: ts-jest under `tsconfig.jest.json`). In non-strict mode
   * `undefined extends string` evaluates to `true`, which would leak
   * `DeleteMessageShape<undefined>` into `EntityDeleteMessage` for any
   * record entity declared without a `deleteType` (cards, deckProperties).
   * `[DeleteType] extends [undefined]` short-circuits to `never` first
   * — the only way `[X] extends [undefined]` is true is when `X` is
   * exactly `undefined`, regardless of `strictNullChecks`. */
  readonly DeleteMessage: [Kind] extends ['record']
    ? [DeleteType] extends [undefined]
      ? never
      : [DeleteType] extends [string]
        ? DeleteMessageShape<DeleteType & string>
        : never
    : never;
  /** Phantom carrier for the delete broadcast (record entities with `deleteBroadcastType`). */
  readonly DeleteBroadcastMessage: [Kind] extends ['record']
    ? [DeleteBroadcastType] extends [undefined]
      ? never
      : [DeleteBroadcastType] extends [string]
        ? DeleteMessageShape<DeleteBroadcastType & string>
        : never
    : never;
  /** Phantom carrier for bulk push (record entities only). */
  readonly BulkPushMessage: [Kind] extends ['record']
    ? BulkPushMessageShape<
        `${PushType extends `${infer Stem}-push` ? Stem : PushType}-bulk-push`,
        T
      >
    : never;
  /** Phantom carrier for bulk broadcast. */
  readonly BulkBroadcastMessage: [Kind] extends ['record']
    ? BulkBroadcastMessageShape<
        `${BroadcastType extends `${infer Stem}-updated` ? Stem : BroadcastType}-bulk-updated`,
        T
      >
    : never;
  /** Phantom carrier for bulk delete (record entities with `deleteType`). */
  readonly BulkDeleteMessage: [Kind] extends ['record']
    ? [DeleteType] extends [undefined]
      ? never
      : [DeleteType] extends [string]
        ? BulkDeleteMessageShape<`${DeleteType extends `${infer Stem}-delete` ? Stem : DeleteType}-bulk-delete`>
        : never
    : never;
  /** Phantom carrier for bulk delete broadcast. */
  readonly BulkDeleteBroadcastMessage: [Kind] extends ['record']
    ? [DeleteBroadcastType] extends [undefined]
      ? never
      : [DeleteBroadcastType] extends [string]
        ? BulkDeleteMessageShape<`${DeleteBroadcastType extends `${infer Stem}-deleted` ? Stem : DeleteBroadcastType}-bulk-deleted`>
        : never
    : never;
};

export function defineEntity<
  T,
  Name extends string,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
>(
  opts: DefineArrayOpts<T, Name, PushType, BroadcastType, PushDataKey, BroadcastDataKey>,
): DefineEntityResult<
  T,
  Name,
  'array',
  PushType,
  BroadcastType,
  PushDataKey,
  BroadcastDataKey
>;
export function defineEntity<
  T,
  Name extends string,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
  IndexById extends boolean = false,
>(
  opts: Omit<
    DefineCollectionOpts<T, Name, PushType, BroadcastType, PushDataKey, BroadcastDataKey>,
    'indexById'
  > & { indexById?: IndexById },
): DefineEntityResult<
  T,
  Name,
  'collection',
  PushType,
  BroadcastType,
  PushDataKey,
  BroadcastDataKey,
  undefined,
  undefined,
  false,
  IndexById
>;
export function defineEntity<
  T,
  Name extends string,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
>(
  opts: DefineValueOpts<T, Name, PushType, BroadcastType, PushDataKey, BroadcastDataKey>,
): DefineEntityResult<
  T,
  Name,
  'value',
  PushType,
  BroadcastType,
  PushDataKey,
  BroadcastDataKey
>;
export function defineEntity<
  T,
  Name extends string,
  PushType extends string = `${Name}-push`,
  BroadcastType extends string = `${Name}-updated`,
  PushDataKey extends string = Name,
  BroadcastDataKey extends string = Name,
  DeleteType extends string | undefined = undefined,
  DeleteBroadcastType extends string | undefined = undefined,
  Nested extends boolean = false,
>(
  opts: Omit<
    DefineRecordOpts<
      T,
      Name,
      PushType,
      BroadcastType,
      PushDataKey,
      BroadcastDataKey,
      DeleteType,
      DeleteBroadcastType
    >,
    'nested'
  > & { nested?: Nested },
): DefineEntityResult<
  T,
  Name,
  'record',
  PushType,
  BroadcastType,
  PushDataKey,
  BroadcastDataKey,
  DeleteType,
  DeleteBroadcastType,
  Nested,
  false
>;
export function defineEntity<T, Name extends string>(
  opts:
    | DefineArrayOpts<T, Name>
    | DefineCollectionOpts<T, Name>
    | DefineValueOpts<T, Name>
    | DefineRecordOpts<T, Name>,
): DefineEntityResult<T, Name, EntityKind> {
  const { name, type: kind, schema: zodSchema } = opts;

  const isArrayLike = kind === 'array' || kind === 'collection';
  const isRecord = kind === 'record';

  const config: Record<string, unknown> = {
    name,
    type: kind,
    initial: isArrayLike ? [] : isRecord ? {} : (opts as { initial?: unknown }).initial,
  };

  if (isArrayLike && 'primaryKey' in opts) {
    config.primaryKey = (opts as { primaryKey: string }).primaryKey;
  }
  if (kind === 'collection' && 'indexById' in opts) {
    config.indexById = (opts as { indexById?: boolean }).indexById;
  }

  if (isRecord) {
    const r = opts as DefineRecordOpts<T, Name>;
    config.entryKey = r.entryKey;
    config.doPrefix = r.doPrefix;
    config.tsKeyPrefix = r.tsKeyPrefix;
    if (r.mergeEntry) config.mergeEntry = r.mergeEntry;
    if (r.foldBroadcasts) config.foldBroadcasts = true;
    if (r.normalizeServerEntry) config.normalizeServerEntry = r.normalizeServerEntry;
    if (r.serverKeysField) config.serverKeysField = r.serverKeysField;
    if (r.deleteType) config.deleteType = r.deleteType;
    if (r.deleteBroadcastType) config.deleteBroadcastType = r.deleteBroadcastType;
    if (r.decomposeStorageKey) config.decomposeStorageKey = r.decomposeStorageKey;
  }

  if (opts.tsKey) config.tsKey = opts.tsKey;
  if (opts.storageKey) config.storageKey = opts.storageKey;
  if (opts.diffKey) config.diffKey = opts.diffKey;
  if ((opts as { settingsKey?: string }).settingsKey)
    config.settingsKey = (opts as { settingsKey?: string }).settingsKey;
  if (opts.serverAuthored) config.serverAuthored = opts.serverAuthored;
  if (opts.messages) config.messages = opts.messages;
  if (opts.storage) config.storage = opts.storage;
  if (opts.nested) config.nested = opts.nested;
  if (opts.fieldScopes) config.fieldScopes = opts.fieldScopes;
  if (opts.authorize) config.authorize = opts.authorize;
  if (opts.authority) config.authority = opts.authority;
  if (opts.mirror) config.mirror = opts.mirror;
  if (opts.groupStorageKey) config.groupStorageKey = opts.groupStorageKey;
  if (opts.groupMessages) config.groupMessages = opts.groupMessages;
  if (opts.groupDeleteType) config.groupDeleteType = opts.groupDeleteType;
  if (opts.groupDeleteBroadcastType)
    config.groupDeleteBroadcastType = opts.groupDeleteBroadcastType;

  // Forward the Zod schema so defineSyncEntity composes inbound-message
  // validators at define-time. Hot path is just `safeParse`.
  config.validationSchema = zodSchema;

  const entity = defineSyncEntity(
    config as unknown as Parameters<typeof defineSyncEntity<T>>[0],
  );

  return {
    config,
    entity,
    T: undefined as unknown as T,
    schema: zodSchema,
    // Phantom carriers — undefined at runtime, used only by the type system
    // (registry derives `SyncClientMessage` / `SyncServerMessage` unions from these).
    PushMessage: undefined,
    BroadcastMessage: undefined,
    DeleteMessage: undefined,
    DeleteBroadcastMessage: undefined,
  } as unknown as DefineEntityResult<T, Name, EntityKind>;
}
